import { initializeApp } from "firebase/app";
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
} from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  persistentSingleTabManager,
  memoryLocalCache,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  arrayUnion,
  arrayRemove,
  Timestamp,
  onSnapshot,
  orderBy,
  limit,
  increment,
  runTransaction,
  writeBatch,
  DocumentData,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { getStorage, ref, uploadString, getDownloadURL, deleteObject } from "firebase/storage";
import { getMessaging, getToken, onMessage, isSupported, Messaging } from "firebase/messaging";
import { getFunctions, httpsCallable } from "firebase/functions";
import { 
  UserProfile, 
  ActivityItem, 
  Venue, 
  PointsPackage, 
  Event, 
  Message, 
  MessageThread, 
  BostonEvent, 
  PreAggregatedFeedItem, 
  EventComment,
  GameRecord,
  GameHand,
  LeaderboardPlayerStat,
  LeaderboardTeamStat,
  ArenaGameRecord,
  ArenaPlayerStat,
  ArenaMatchMode
} from "./models/FirestoreModels";

import { getApiUrl } from "./config/apiConfig";

export async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has('Authorization') && auth.currentUser) {
    try {
      const token = await auth.currentUser.getIdToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    } catch (_) {}
  }

  // Safety guard: default 8s timeout prevents background network stalls from freezing pages indefinitely
  let timeoutId: any;
  let signal = init?.signal;
  if (!signal && typeof AbortController !== 'undefined') {
    const controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), 8000);
    signal = controller.signal;
  }

  try {
    let response = await fetch(getApiUrl(input), { ...init, headers, signal });
    if (timeoutId) clearTimeout(timeoutId);

    if (response.status === 401 && auth.currentUser) {
      try {
        const freshToken = await auth.currentUser.getIdToken(true);
        if (freshToken) {
          headers.set('Authorization', `Bearer ${freshToken}`);
          response = await fetch(getApiUrl(input), { ...init, headers });
        }
      } catch (e) {
        console.warn('[apiFetch] Token refresh on 401 failed:', e);
      }
    }
    return response;
  } catch (err) {
    if (timeoutId) clearTimeout(timeoutId);
    throw err;
  }
}

// ── IMPORTANT: Replace this with your VAPID key from Firebase Console ──────────
const VAPID_KEY = "BCarcCkdlfH31A2JdAeL8uzzKHBLfy7jIz0HNJeEHKN5eTHGVJhOmp-QleVgyiQGxavnFUGtKSffhnuDViAcU30";
// ─────────────────────────────────────────────────────────────────────────────

import { getDatabase } from "firebase/database";

export const firebaseConfig = {
  apiKey: "AIzaSyDZ3XrIeB_DjYa5AuuHbeYBLoZ5kSzqa-Q",
  authDomain: "atlcardsharks-a9914.firebaseapp.com",
  databaseURL: "https://atlcardsharks-a9914-default-rtdb.firebaseio.com",
  projectId: "atlcardsharks-a9914",
  storageBucket: "atlcardsharks-a9914.firebasestorage.app",
  messagingSenderId: "994592405825",
  appId: "1:994592405825:web:5af7f803dc67c60cad49c0",
  measurementId: "G-T7MCV3WZ46",
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

const isIOSOrSafari = typeof navigator !== 'undefined' && (
  /iPad|iPhone|iPod/.test(navigator.userAgent || '') ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
  (/^((?!chrome|android).)*safari/i.test(navigator.userAgent || ''))
);

let localCacheSetting: any;
try {
  if (isIOSOrSafari && typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined') {
    // WebKit (Safari on iOS) has bugs with multi-tab WebLock concurrency in persistentMultipleTabManager.
    // persistentSingleTabManager provides full IndexedDB offline caching and fast reads without multi-tab deadlocks.
    localCacheSetting = persistentLocalCache({
      tabManager: persistentSingleTabManager({})
    });
  } else if (typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined') {
    localCacheSetting = persistentLocalCache({
      tabManager: persistentMultipleTabManager()
    });
  } else {
    localCacheSetting = memoryLocalCache();
  }
} catch (err) {
  console.warn("[Firestore] Failed to configure persistent local cache, falling back to memory cache:", err);
  localCacheSetting = memoryLocalCache();
}

export const db = initializeFirestore(app, {
  ignoreUndefinedProperties: true,
  ...(localCacheSetting ? { localCache: localCacheSetting } : {})
});
export const storage = getStorage(app);
export const functions = getFunctions(app);
export const rtdb = getDatabase(app);

export const subscribeToAuthToken = (callback: (user: User | null) => void) => {
  return onIdTokenChanged(auth, callback);
};

// --- Utility Functions ---
import {
  compressImage,
  parseDate,
  isMemberUnpaidOrExpired,
  formatRelativeTime,
  teamLabel,
  sanitizeFileName,
  uploadDataUrl,
  uploadScorecardImage,
  fixPhotoUrl,
  getStorageFileUrl,
  loadGoogleMaps
} from "./services/firebaseUtils";
export {
  compressImage,
  parseDate,
  isMemberUnpaidOrExpired,
  formatRelativeTime,
  teamLabel,
  sanitizeFileName,
  uploadDataUrl,
  uploadScorecardImage,
  fixPhotoUrl,
  getStorageFileUrl,
  loadGoogleMaps
};

// --- Messaging & Notification Service ---
import {
  getFCMToken,
  onForegroundMessage,
  savePushToken
} from "./services/notificationService";
export {
  getFCMToken,
  onForegroundMessage,
  savePushToken
};

// --- Authentication Service ---

const provider = new GoogleAuthProvider();

export function listenForAuthChanges(callback: (state: { user: User | null; token: string | null; claims: Record<string, any>; profile: UserProfile | null }) => void): () => void {
  let isCancelled = false;
  let unsubscribeProfile: (() => void) | null = null;
  let profileTimeout: ReturnType<typeof setTimeout> | null = null;

  const unsubscribeAuth = onIdTokenChanged(auth, async (user) => {
    if (unsubscribeProfile) { unsubscribeProfile(); unsubscribeProfile = null; }
    if (profileTimeout) { clearTimeout(profileTimeout); profileTimeout = null; }
    if (isCancelled) return;
    if (!user) { callback({ user: null, token: null, claims: {}, profile: null }); return; }
    try {
      const token = await user.getIdToken();
      if (isCancelled) return;
      const tokenResult = await user.getIdTokenResult();
      if (isCancelled) return;

      // 1. Immediately check localStorage for cached profile
      const localProfileKey = `acs_profile_${user.uid}`;
      let cachedProfile: UserProfile | null = null;
      try {
        const stored = typeof window !== 'undefined' ? localStorage.getItem(localProfileKey) : null;
        if (stored) {
          cachedProfile = JSON.parse(stored);
        }
      } catch (e) {
        console.warn("[listenForAuthChanges] Failed to parse cached profile:", e);
      }

      // If we have a cached profile, emit it immediately so the UI is unblocked with real profile & admin status!
      if (cachedProfile) {
        callback({ user, token, claims: tokenResult?.claims ?? {}, profile: cachedProfile });
      }

      let hasEmittedInitial = !!cachedProfile;

      // Proactively fetch authoritative member profile from PostgreSQL
      getMemberProfile(user.uid).then(pgProfile => {
        if (isCancelled) return;
        if (pgProfile) {
          hasEmittedInitial = true;
          try {
            localStorage.setItem(localProfileKey, JSON.stringify(pgProfile));
          } catch (_) {}
          callback({ user, token, claims: tokenResult?.claims ?? {}, profile: pgProfile });
        }
      }).catch(e => console.warn("[listenForAuthChanges] Proactive Postgres profile fetch warning:", e));

      // Safety guard: if onSnapshot takes longer than 6s (cellular lag), unblock the app with cached profile
      profileTimeout = setTimeout(() => {
        if (!hasEmittedInitial && !isCancelled) {
          hasEmittedInitial = true;
          console.warn("[listenForAuthChanges] Profile query slow to resolve; unblocking UI with cached auth profile.");
          callback({ user, token, claims: tokenResult?.claims ?? {}, profile: cachedProfile });
        }
      }, 6000);

      const profileRef = doc(db, "members", user.uid);
      unsubscribeProfile = onSnapshot(profileRef, (snapshot) => {
        if (profileTimeout) { clearTimeout(profileTimeout); profileTimeout = null; }
        hasEmittedInitial = true;
        if (isCancelled) {
          if (unsubscribeProfile) { unsubscribeProfile(); unsubscribeProfile = null; }
          return;
        }
        const profileData = snapshot.exists() ? normalizeMember(user.uid, snapshot.data()) : null;
        if (profileData && typeof window !== 'undefined') {
          try {
            localStorage.setItem(localProfileKey, JSON.stringify(profileData));
          } catch (_) {}
        }
        callback({ user, token, claims: tokenResult?.claims ?? {}, profile: profileData || cachedProfile });
      }, (error) => {
        if (profileTimeout) { clearTimeout(profileTimeout); profileTimeout = null; }
        hasEmittedInitial = true;
        if (isCancelled) return;
        console.error("Firestore profile real-time fetch failed:", error);
        callback({ user, token, claims: tokenResult?.claims ?? {}, profile: cachedProfile });
      });
    } catch (e) {
      if (profileTimeout) { clearTimeout(profileTimeout); profileTimeout = null; }
      if (isCancelled) return;
      console.error("Failed to get ID token:", e);
      callback({ user, token: null, claims: {}, profile: null });
    }
  });
  return () => {
    isCancelled = true;
    if (profileTimeout) { clearTimeout(profileTimeout); profileTimeout = null; }
    unsubscribeAuth();
    if (unsubscribeProfile) {
      unsubscribeProfile();
      unsubscribeProfile = null;
    }
  };
}

export async function getCurrentIdToken(forceRefresh: boolean = false): Promise<string | null> {
  const user = auth.currentUser;
  return user ? await user.getIdToken(forceRefresh) : null;
}

export async function syncMemberProfileToPostgres(uid: string, profileData: any): Promise<boolean> {
  try {
    const token = await getCurrentIdToken();
    const res = await apiFetch(`/api/members/${encodeURIComponent(uid)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(profileData)
    });
    return res.ok;
  } catch (err) {
    console.warn(`[syncMemberProfileToPostgres] Failed to sync profile to Postgres for ${uid}:`, err);
    return false;
  }
}

async function updateLastLogin(uid: string): Promise<void> {
  try {
    const nowIso = new Date().toISOString();
    // Update PostgreSQL
    await syncMemberProfileToPostgres(uid, { lastLoginAt: nowIso });
    // Update Firestore for backward compatibility
    await updateDoc(doc(db, "members", uid), {
      lastLoginAt: nowIso,
      updatedAt: new Date()
    });
  } catch (e) {
    console.warn("[updateLastLogin] Failed to record login timestamp:", e);
  }
}

export async function signIn(email: string, password: string): Promise<{ success: boolean; isAdmin?: boolean; message?: string }> {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const profile = await getMemberProfile(userCredential.user.uid);
    if (profile?.isLocked || profile?.isBanned) {
        await signOut(auth);
        return { success: false, message: "ACCOUNT_INACTIVE" };
    }
    await updateLastLogin(userCredential.user.uid);
    const tokenResult = await userCredential.user.getIdTokenResult();
    return { success: true, isAdmin: tokenResult.claims.role === 'Admin' || tokenResult.claims.admin === true };
  } catch (error: any) {
    return { success: false, message: error.message };
  }
}

export async function signInWithGoogle(): Promise<{ success: boolean; profileComplete?: boolean; isAdmin?: boolean; message?: string }> {
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    let existingProfile = await getMemberProfile(user.uid);
    if (!existingProfile) {
        const photoUrl = user.photoURL ?? "";
        const displayName = user.displayName || user.email?.split('@')[0] || "New Member";
        const newMemberPayload = {
            id: user.uid,
            email: user.email ?? "",
            firstName: "",
            lastName: "",
            nickname: displayName,
            displayName: displayName,
            photoUrl: photoUrl,
            avatarUrl: photoUrl,
            phone: "",
            address: "",
            city: "",
            state: "",
            bio: "",
            joinedDate: new Date().toISOString(),
            isLocked: false,
            isBanned: false,
            isAdmin: false,
            isPaid: false
        };

        // 1. Authoritative write to PostgreSQL
        await syncMemberProfileToPostgres(user.uid, newMemberPayload);

        // 2. Backward compatibility write to Firestore
        try {
            await setDoc(doc(db, "members", user.uid), newMemberPayload);
        } catch (_) {}

        // Invalidate member cache so directory updates immediately
        invalidateMembersCache();

        // Announce the new member in the feed
        await writeFeedItem({
            type: "join",
            actorId: user.uid,
            actorDisplayName: displayName,
            actorAvatarUrl: photoUrl,
            body: `just entered the lair via Google...say hi`
        });

        try {
            await logPreAggregatedActivity("join", "atlcardsharks", "Atlanta Card Sharks", photoUrl);
        } catch (e) {
            console.warn("Failed to log preaggregated join activity:", e);
        }
    }
    const profile = await getMemberProfile(user.uid);
    if (profile?.isLocked || profile?.isBanned) {
        await signOut(auth);
        return { success: false, message: "ACCOUNT_INACTIVE" };
    }
    await updateLastLogin(user.uid);
    const tokenResult = await user.getIdTokenResult();
    return { success: true, profileComplete: isProfileComplete(profile), isAdmin: tokenResult.claims.role === 'Admin' || tokenResult.claims.admin === true };
  } catch (error: any) {
    return { success: false, message: error.message };
  }
}

export async function signUp(email: string, password: string, additionalData: any): Promise<{ success: boolean; message?: string }> {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    let photoUrl = additionalData.photoUrl ?? "";
    if (additionalData.profilePhotoDataUrl) {
      const compressedDataUrl = await compressImage(additionalData.profilePhotoDataUrl);
      photoUrl = await uploadDataUrl(`profile-photos/${user.uid}/profile-photo`, compressedDataUrl);
    }

    const displayName = additionalData.nickname || `${additionalData.firstName || ''} ${additionalData.lastName || ''}`.trim() || "New Member";

    const memberPayload = {
      ...additionalData,
      id: user.uid,
      profilePhotoDataUrl: null,
      photoUrl,
      avatarUrl: photoUrl,
      displayName,
      email,
      joinedDate: new Date().toISOString(),
      isLocked: false,
      isBanned: false,
      isAdmin: false,
      isPaid: false
    };

    // 1. Authoritative write to PostgreSQL
    await syncMemberProfileToPostgres(user.uid, memberPayload);

    // 2. Backward compatibility write to Firestore
    try {
      await setDoc(doc(db, "members", user.uid), memberPayload);
    } catch (_) {}

    // Invalidate member cache so directory updates immediately
    invalidateMembersCache();

    // Announce the new member in the feed
    await writeFeedItem({ 
        type: "join", 
        actorId: user.uid, 
        actorDisplayName: displayName, 
        actorAvatarUrl: photoUrl, 
        body: `just entered the lair...say hi` 
    });

    try {
        await logPreAggregatedActivity("join", "atlcardsharks", "Atlanta Card Sharks", photoUrl);
    } catch (e) {
        console.warn("Failed to log preaggregated join activity:", e);
    }

    return { success: true };
  } catch (error: any) {
    return { success: false, message: error.message };
  }
}

export function clearAllModuleCaches(): void {
  try {
    invalidateMembersCache();
    invalidateUpcomingEventsCache();
    invalidateAdminEventsCache();
    invalidateGameRecordsCache();
    invalidateArenaGameRecordsCache();
    invalidateLeaderboardCache();
    invalidateArenaLeaderboardCache();
  } catch (e) {
    console.warn('[clearAllModuleCaches] Failed to clear module caches:', e);
  }
}

export async function logOut(): Promise<void> {
  const user = auth.currentUser;
  if (user) {
    try {
      const { clearLobbyPresence } = await import('./arena/state/ArenaFriendsService');
      clearLobbyPresence(user.uid);
    } catch {}
    try {
      const token = await getFCMToken();
      if (token) {
        const memberDocRef = doc(db, 'members', user.uid);
        const userDocRef = doc(db, 'users', user.uid);
        await Promise.allSettled([
          updateDoc(memberDocRef, {
            pushTokens: arrayRemove(token),
            fcmTokens: arrayRemove(token)
          }),
          updateDoc(userDocRef, {
            pushTokens: arrayRemove(token),
            fcmTokens: arrayRemove(token)
          })
        ]);
        console.log('[logOut] Removed FCM token from Firestore for user:', user.uid);
      }
    } catch (e) {
      console.warn('[logOut] Failed to remove FCM token on logout:', e);
    }
  }
  clearAllModuleCaches();
  await signOut(auth);
}

export async function resetPassword(email: string): Promise<{ success: boolean; message?: string }> {
  if (!email) return { success: false, message: "Email is required." };
  try {
    // Call our onRequest Cloud Function directly via fetch.
    // This bypasses Firebase SDK referrer restrictions that block unauthenticated
    // calls (e.g. from the login page) when API key has HTTP referrer restrictions.
    const resp = await fetch(
      "https://us-central1-atlcardsharks-a9914.cloudfunctions.net/sendPasswordResetEmail",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }
    );
    const data = await resp.json();
    if (!resp.ok || !data.success) {
      return { success: false, message: data.message || "Failed to send reset email." };
    }
    return { success: true };
  } catch (e: any) {
    return { success: false, message: e.message };
  }
}


// --- Audit Logging & Activity Feed ---

export async function writeAuditLog(action: string, targetId: string, details: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) return;
    try {
        await addDoc(collection(db, "audit_logs"), {
            userId: user.uid,
            userEmail: user.email ?? "",
            userDisplayName: user.displayName ?? "Member",
            action,
            targetId,
            details,
            timestamp: new Date()
        });
    } catch (e) {
        console.warn("[writeAuditLog] Error:", e);
    }
}

async function writeFeedItem(item: Partial<ActivityItem>): Promise<void> {
    try {
        await addDoc(collection(db, "activityFeed"), {
            ...item,
            createdAt: new Date()
        });
    } catch (e) {
        console.warn("[writeFeedItem] Error:", e);
    }
}


function normalizeFeedDoc(d: QueryDocumentSnapshot<DocumentData>): ActivityItem {
  const data = d.data();
  const raw = data.createdAt;
  const createdAt = parseDate(raw) || new Date();
  return {
    id: d.id,
    type: data.type ?? "general",
    actorId: data.actorId ?? "",
    actorDisplayName: data.actorDisplayName ?? "Member",
    actorAvatarUrl: fixPhotoUrl(data.actorAvatarUrl ?? ""),
    targetId: data.targetId ?? "",
    targetTitle: data.targetTitle ?? "",
    targetUrl: data.targetUrl ?? "",
    body: data.body ?? "",
    metadata: data.metadata ?? {},
    likesCount: data.likesCount ?? 0,
    createdAt: createdAt.toISOString(),
  };
}

function normalizeMember(id: string, data: DocumentData): UserProfile {
  const firstName = data.firstName ?? "";
  const lastName = data.lastName ?? "";
  const nickname = data.nickname ?? "";
  const email = data.email ?? "";
  const nameCombined = `${firstName} ${lastName}`.trim();
  const displayName = data.displayName || data.fullName || data.name || data.userName || nickname || nameCombined || (email ? email.split('@')[0] : "") || "Member";
  const photoUrl = fixPhotoUrl(data.photoUrl ?? "");
  const avatarUrl = fixPhotoUrl(data.avatarUrl ?? "");

  const galleryPhotoUrls = Array.isArray(data.galleryPhotoUrls) ? data.galleryPhotoUrls.map(url => fixPhotoUrl(url)) : [];

  const isSuper = data.isSuper === true;
  const adminRole = data.adminRole ?? "";
  const permissions = data.permissions && typeof data.permissions === 'object' ? data.permissions : {};
  const hasPermissions = Object.values(permissions).some(Boolean);
  const isAdmin = isSuper || data.isAdmin === true || ["organizer", "event_host", "tech_helper"].includes(adminRole) || hasPermissions;

  return {
    id, firstName, lastName, nickname, displayName, email, photoUrl,
    avatarUrl, phone: data.phone ?? "", address: data.address ?? "",
    city: data.city ?? "", joinedDate: data.joinedDate ?? "",
    isPaid: data.isPaid === true, isLocked: data.isLocked === true,
    isBanned: data.isBanned === true, isAdmin, isSuper,
    adminRole, permissions, bio: data.bio ?? "",
    lastLoginAt: data.lastLoginAt ?? "",
    galleryPhotoUrls, followersCount: data.followersCount ?? 0, followingCount: data.followingCount ?? 0,
    membershipExpirationDate: data.membershipExpirationDate ?? null,
    currentMembershipId: data.currentMembershipId ?? "",
    is_deleted: data.is_deleted === true
  };
}

function normalizeEventDate(value: any): Date | null {
    return parseDate(value);
}

function formatEventTime(startTime: string, endTime: string, hasEndTime: boolean): string {
    const formattedStart = formatClockTime(startTime);
    const formattedEnd = formatClockTime(endTime);
    if (!formattedStart) return "Time TBD";
    if (hasEndTime && formattedEnd) return `${formattedStart} - ${formattedEnd}`;
    return formattedStart;
}

function formatClockTime(value: any): string {
    if (!value) return "";
    const text = String(value);
    const parts = text.split(":");
    const hour = Number(parts[0]);
    const minute = Number(parts[1] ?? "0");
    if (isNaN(hour) || isNaN(minute)) return text;
    const suffix = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

function normalizeEvent(id: string, data: DocumentData, currentMemberId: string | null | undefined, memberProfiles: Record<string, UserProfile> = {}): Event {
    const rawDate = data.date ?? data.eventDate ?? data.startDate ?? data.startsAt ?? data.dateTime ?? null;
    const eventDate = normalizeEventDate(rawDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    let isUpcoming = true;
    if (typeof rawDate === "string" && /^\d{4}-\d{2}-\d{2}/.test(rawDate)) {
        isUpcoming = rawDate.substring(0, 10) >= todayStr;
    } else if (eventDate !== null) {
        isUpcoming = eventDate >= today;
    }

    const photos = (Array.isArray(data.photoUrls) ? data.photoUrls : []).filter((u: any) => typeof u === 'string' && u.trim().length > 0).map((u: string) => fixPhotoUrl(u));
    const mainPhoto = fixPhotoUrl(data.photoUrl || data.coverUrl || data.imageUrl || data.backgroundImageUrl);
    if (mainPhoto && !photos.includes(mainPhoto)) photos.unshift(mainPhoto);
    const venuePhoto = fixPhotoUrl(data.venuePhotoUrl || data.venue?.photoUrl || data.venuePhoto || "");
    if (venuePhoto && !photos.includes(venuePhoto)) photos.push(venuePhoto);

    const rsvps = data.rsvps ?? {};
    const attendees: { going: any[]; maybe: any[]; declined: any[] } = { going: [], maybe: [], declined: [] };
    if (typeof rsvps === 'object' && rsvps !== null && !Array.isArray(rsvps)) {
        Object.entries(rsvps).forEach(([mId, r]: [string, any]) => {
            const profile = memberProfiles[mId];
            if (profile?.isBanned) {
                return;
            }
            const status = typeof r === 'string' ? r.toLowerCase() : (r?.status ? String(r.status).toLowerCase() : "");
            const item = {
                memberId: mId,
                displayName: profile?.displayName || profile?.nickname || (typeof r === 'object' ? r?.displayName : "") || "Member",
                avatarUrl: fixPhotoUrl(profile?.avatarUrl || profile?.photoUrl || (typeof r === 'object' ? r?.avatarUrl : "") || ""),
                status
            };
            if ((attendees as any)[status]) (attendees as any)[status].push(item);
        });
    }

    // Fallback: if going attendees is empty, pull from pre-existing goingAttendees array if present
    if (attendees.going.length === 0 && Array.isArray(data.goingAttendees)) {
        attendees.going = data.goingAttendees.map((a: any) => {
            const mId = typeof a === 'string' ? a : (a.memberId || a.id || a.uid || "");
            const profile = memberProfiles[mId];
            return {
                memberId: mId,
                displayName: profile?.displayName || profile?.nickname || (typeof a === 'object' ? (a.displayName || a.name) : "") || "Member",
                avatarUrl: fixPhotoUrl(profile?.avatarUrl || profile?.photoUrl || (typeof a === 'object' ? (a.avatarUrl || a.photoUrl) : "") || ""),
                status: 'going'
            };
        });
    }

    if (attendees.maybe.length === 0 && Array.isArray(data.maybeAttendees)) {
        attendees.maybe = data.maybeAttendees.map((a: any) => {
            const mId = typeof a === 'string' ? a : (a.memberId || a.id || a.uid || "");
            const profile = memberProfiles[mId];
            return {
                memberId: mId,
                displayName: profile?.displayName || profile?.nickname || (typeof a === 'object' ? (a.displayName || a.name) : "") || "Member",
                avatarUrl: fixPhotoUrl(profile?.avatarUrl || profile?.photoUrl || (typeof a === 'object' ? (a.avatarUrl || a.photoUrl) : "") || ""),
                status: 'maybe'
            };
        });
    }

    if (attendees.declined.length === 0 && Array.isArray(data.declinedAttendees)) {
        attendees.declined = data.declinedAttendees.map((a: any) => {
            const mId = typeof a === 'string' ? a : (a.memberId || a.id || a.uid || "");
            const profile = memberProfiles[mId];
            return {
                memberId: mId,
                displayName: profile?.displayName || profile?.nickname || (typeof a === 'object' ? (a.displayName || a.name) : "") || "Member",
                avatarUrl: fixPhotoUrl(profile?.avatarUrl || profile?.photoUrl || (typeof a === 'object' ? (a.avatarUrl || a.photoUrl) : "") || ""),
                status: 'declined'
            };
        });
    }

    const hostIds = Array.isArray(data.hostMemberIds) ? data.hostMemberIds : [];
    const hosts = hostIds.map(mId => memberProfiles[mId]).filter(Boolean);

    return {
        id, title: data.title ?? data.name ?? "Untitled Event",
        date: eventDate ? eventDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : "Date TBD",
        startTime: data.startTime ?? "", endTime: data.endTime ?? "", hasEndTime: data.hasEndTime === true,
        timeDisplay: formatEventTime(data.startTime, data.endTime, data.hasEndTime),
        sortDate: eventDate?.toISOString() ?? null,
        category: data.category ?? data.type ?? "Event",
        description: data.description ?? data.details ?? "",
        venueName: data.venueName ?? "", venueAddress: data.venueAddress ?? "",
        venuePhotoUrl: venuePhoto || fixPhotoUrl(data.venuePhotoUrl) || "", photoUrls: photos,
        isPublished: data.isPublished !== false, isActive: data.isActive !== false,
        isFeatured: data.isFeatured === true, hostMemberIds: hostIds,
        hosts,
        hostProfiles: hosts.map(h => ({
            id: h.id,
            displayName: h.displayName!,
            avatarUrl: h.photoUrl
        })),
        isUpcoming, 
        rsvpStatus: currentMemberId 
            ? (typeof rsvps[currentMemberId] === 'string' 
                ? rsvps[currentMemberId] 
                : rsvps[currentMemberId]?.status 
                    ? String(rsvps[currentMemberId].status)
                    : attendees.going.some((a: any) => a.memberId === currentMemberId || a.id === currentMemberId || a.uid === currentMemberId)
                        ? "going"
                        : attendees.maybe.some((a: any) => a.memberId === currentMemberId || a.id === currentMemberId || a.uid === currentMemberId)
                            ? "maybe"
                            : attendees.declined.some((a: any) => a.memberId === currentMemberId || a.id === currentMemberId || a.uid === currentMemberId)
                                ? "declined"
                                : "")
            : "",
        goingCount: attendees.going.length, maybeCount: attendees.maybe.length, declinedCount: attendees.declined.length,
        goingAttendees: attendees.going, maybeAttendees: attendees.maybe, declinedAttendees: attendees.declined,
        ratingCount: data.ratingCount ?? 0,
        averageRating: data.averageRating ?? 0,
        ratingQuestions: data.ratingQuestions ?? []
    };
}

function normalizeMessage(id: string, data: DocumentData): Message {
    const createdAt = parseDate(data.createdAt);
    return {
        id, senderId: data.senderId ?? "", senderName: data.senderName ?? "Member",
        senderAvatarUrl: fixPhotoUrl(data.senderAvatarUrl), text: data.text ?? "",
        imageUrl: fixPhotoUrl(data.imageUrl), createdAt: createdAt?.toISOString() ?? "",
        timeAgo: formatClockTime(`${String(createdAt?.getHours() ?? 0).padStart(2, '0')}:${String(createdAt?.getMinutes() ?? 0).padStart(2, '0')}`)
    };
}

function normalizeThread(id: string, data: DocumentData): MessageThread {
    return {
        id, memberIds: data.memberIds ?? [], title: data.title ?? "Conversation",
        eventId: data.eventId ?? "", lastMessage: data.lastMessage ?? "",
        updatedAt: parseDate(data.updatedAt)?.toISOString() ?? "",
        lastRead: data.lastRead ?? {}
    };
}

async function propagateMemberUpdates(uid: string, newName: string, newPhoto: string, oldName: string, oldPhoto: string): Promise<void> {
  try {
    const escapedOldName = oldName ? oldName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') : "";
    const nameRegex = escapedOldName ? new RegExp(escapedOldName, 'gi') : null;

    // 1. Update Activity Feed items (Actor fields)
    const actorFeedQuery = query(collection(db, "activityFeed"), where("actorId", "==", uid));
    const actorFeedSnap = await getDocs(actorFeedQuery);
    for (const feedDoc of actorFeedSnap.docs) {
      const feedData = feedDoc.data();
      const updates: any = {
        actorDisplayName: newName,
        actorAvatarUrl: newPhoto
      };
      if (feedData.body && nameRegex) {
        updates.body = feedData.body.replace(nameRegex, newName);
      }
      await updateDoc(doc(db, "activityFeed", feedDoc.id), updates);
    }

    // 3. Update Activity Feed items (Target fields)
    const targetFeedQuery = query(collection(db, "activityFeed"), where("targetId", "==", uid));
    const targetFeedSnap = await getDocs(targetFeedQuery);
    for (const feedDoc of targetFeedSnap.docs) {
      const feedData = feedDoc.data();
      const updates: any = { targetTitle: newName };
      if (feedData.body && nameRegex) {
        updates.body = feedData.body.replace(nameRegex, newName);
      }
      if (feedData.metadata) {
        const metadata = { ...feedData.metadata };
        if (metadata.targetAvatarUrl !== undefined) {
          metadata.targetAvatarUrl = newPhoto;
          updates.metadata = metadata;
        }
      }
      await updateDoc(doc(db, "activityFeed", feedDoc.id), updates);
    }

    // 4. Update Game Events (Bostons)
    const gamesQuery = query(collection(db, "gameEvents"), where("participantMemberIds", "array-contains", uid));
    const gamesSnap = await getDocs(gamesQuery);
    for (const gameDoc of gamesSnap.docs) {
      const gameData = gameDoc.data();
      let changed = false;
      
      const winningTeam = gameData.winningTeam || [];
      const updatedWinningTeam = winningTeam.map((player: any) => {
        if (player.memberId === uid && player.displayName !== newName) {
          changed = true;
          return { ...player, displayName: newName };
        }
        return player;
      });

      const losingTeam = gameData.losingTeam || [];
      const updatedLosingTeam = losingTeam.map((player: any) => {
        if (player.memberId === uid && player.displayName !== newName) {
          changed = true;
          return { ...player, displayName: newName };
        }
        return player;
      });

      if (changed) {
        await updateDoc(doc(db, "gameEvents", gameDoc.id), {
          winningTeam: updatedWinningTeam,
          losingTeam: updatedLosingTeam
        });
      }
    }

    // 5. Update preAggregatedFeed items
    const preFeedSnap = await getDocs(collection(db, "preAggregatedFeed"));
    for (const preDoc of preFeedSnap.docs) {
      const preData = preDoc.data();
      let changed = false;
      const updates: any = {};

      if (preData.headline && nameRegex && nameRegex.test(preData.headline)) {
        updates.headline = preData.headline.replace(nameRegex, newName);
        changed = true;
      }

      if (preData.targetName && nameRegex && nameRegex.test(preData.targetName)) {
        updates.targetName = preData.targetName.replace(nameRegex, newName);
        changed = true;
      }

      if (Array.isArray(preData.primaryActors)) {
        const updatedActors = preData.primaryActors.map((actor: any) => {
          if (actor.id === uid) {
            let actorChanged = false;
            const updatedActor = { ...actor };
            if (actor.name !== newName) {
              updatedActor.name = newName;
              actorChanged = true;
            }
            if (actor.avatarUrl !== newPhoto) {
              updatedActor.avatarUrl = newPhoto;
              actorChanged = true;
            }
            if (actorChanged) changed = true;
            return updatedActor;
          }
          return actor;
        });
        if (changed) {
          updates.primaryActors = updatedActors;
        }
      }

      if (changed) {
        await updateDoc(doc(db, "preAggregatedFeed", preDoc.id), updates);
      }
    }
  } catch (e) {
    console.warn("[propagateMemberUpdates] Error propagating profile updates:", e);
  }
}

// --- Exported Operations ---

export async function saveMemberProfile(profile: any): Promise<{ success: boolean; message: string }> {
  if (!auth.currentUser) return { success: false, message: "Not signed in." };
  try {
    const uid = auth.currentUser.uid;
    const { id, displayName, ...data } = profile;
    if (data.photoUrl) {
      data.avatarUrl = data.photoUrl;
    } else if (data.avatarUrl) {
      data.photoUrl = data.avatarUrl;
    }

    // Get the current document state to check if name or photo actually changed
    const originalSnap = await getDoc(doc(db, "members", uid));
    const original = originalSnap.exists() ? originalSnap.data() : {};
    
    // Resolve display names dynamically to check for updates and trigger propagation
    const newName = data.nickname || `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email || "Member";
    const newPhoto = data.photoUrl || original.photoUrl || "";
    
    const oldName = original.nickname || `${original.firstName || ''} ${original.lastName || ''}`.trim() || original.email || "Member";
    const oldPhoto = original.photoUrl || "";

    await setDoc(doc(db, "members", uid), {
      ...data,
      updatedAt: new Date()
    }, { merge: true });

    // Sync member profile updates to PostgreSQL
    try {
      await apiFetch(`/api/members/${encodeURIComponent(uid)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          displayName: newName,
          photoUrl: newPhoto,
          avatarUrl: newPhoto
        })
      });
    } catch (pgErr) {
      console.warn("[saveMemberProfile] Failed to sync to PostgreSQL:", pgErr);
    }

    // Propagate only if a change occurred
    if (newName !== oldName || newPhoto !== oldPhoto) {
      await propagateMemberUpdates(uid, newName, newPhoto, oldName, oldPhoto);
    }

    invalidateMembersCache();

    return { success: true, message: "Profile saved successfully." };
  } catch (e: any) {
    return { success: false, message: e.message };
  }
}

let cachedBostonEvents: { data: BostonEvent[]; timestamp: number } | null = null;
let inflightBostonPromise: Promise<BostonEvent[]> | null = null;
const BOSTON_EVENTS_CACHE_TTL_MS = 900_000; // 15 minutes

export function invalidateBostonEventsCache() {
  cachedBostonEvents = null;
  inflightBostonPromise = null;
  removeSessionCached('acs_cache_bostons');
}

export async function getBostonEvents(forceRefresh = false): Promise<BostonEvent[]> {
  const now = Date.now();
  if (forceRefresh) {
    cachedBostonEvents = null;
    inflightBostonPromise = null;
    removeSessionCached('acs_cache_bostons');
  } else {
    if (cachedBostonEvents && (now - cachedBostonEvents.timestamp < BOSTON_EVENTS_CACHE_TTL_MS)) {
      return cachedBostonEvents.data;
    }
    const sessionData = getSessionCached<BostonEvent[]>('acs_cache_bostons', BOSTON_EVENTS_CACHE_TTL_MS);
    if (sessionData) {
      cachedBostonEvents = { data: sessionData, timestamp: now };
      return sessionData;
    }
  }

  if (inflightBostonPromise) {
    return inflightBostonPromise;
  }

  inflightBostonPromise = (async () => {
    try {
      const bostonQuery = query(collection(db, "gameEvents"), where("type", "==", "boston"));
      const snapshot = await getDocs(bostonQuery);
      const memberProfiles: Record<string, UserProfile> = {};
      if (auth.currentUser) {
        try {
          const members = await getMembers();
          members.forEach(m => {
            if (m.id) memberProfiles[m.id] = m;
          });
        } catch (err) {
          console.warn("Failed to fetch members for Boston events:", err);
        }
      }

      const result = snapshot.docs
        .map((doc) => {
            const raw = doc.data();
            const ev = normalizeBostonEvent(doc.id, raw);
            ev.winningMembers = (raw.winningMemberIds ?? []).map((id: string) => memberProfiles[id]).filter(Boolean);
            ev.losingMembers = (raw.losingMemberIds ?? []).map((id: string) => memberProfiles[id]).filter(Boolean);
            ev.participants = (raw.participantMemberIds ?? []).map((id: string) => memberProfiles[id]).filter(Boolean);
            return ev;
        })
        .sort((left, right) => right.playedAt.localeCompare(left.playedAt));

      cachedBostonEvents = { data: result, timestamp: Date.now() };
      setSessionCached('acs_cache_bostons', result);
      return result;
    } catch (e) {
      console.warn("[getBostonEvents] Failed to load Boston events:", e);
      return cachedBostonEvents?.data || [];
    } finally {
      inflightBostonPromise = null;
    }
  })();

  return inflightBostonPromise;
}

export async function getBostonPerformanceStats(): Promise<{ bostonsWon: string; bostonsAgainst: string; bostonRank: string } | null> {
  if (!auth.currentUser) return null;
  const events = await getBostonEvents();
  const uid = auth.currentUser.uid;
  const won = events.filter((event) => (event.winningMemberIds ?? []).includes(uid)).length;
  const lost = events.filter((event) => (event.losingMemberIds ?? []).includes(uid)).length;
  const leaderboard = new Map<string, number>();

  for (const event of events) {
    for (const memberId of (event.winningMemberIds ?? [])) {
      leaderboard.set(memberId, (leaderboard.get(memberId) ?? 0) + 1);
    }
  }

  const ranked = [...leaderboard.entries()].sort((left, right) => right[1] - left[1]);
  const rankIndex = ranked.findIndex(([memberId]) => memberId === uid);

  return {
    bostonsWon: won.toLocaleString(),
    bostonsAgainst: lost.toLocaleString(),
    bostonRank: rankIndex >= 0 ? `${rankIndex + 1}` : "-"
  };
}

function normalizeBostonEvent(id: string, data: DocumentData): BostonEvent {
  const playedAt = parseDate(data.playedAt);
  return {
    id,
    winningMemberIds: data.winningMemberIds ?? [],
    losingMemberIds: data.losingMemberIds ?? [],
    participantMemberIds: data.participantMemberIds ?? [],
    winningTeam: data.winningTeam ?? [],
    losingTeam: data.losingTeam ?? [],
    bidLabel: data.bidLabel ?? "",
    bidBooks: Number(data.bidBooks ?? 0),
    bidType: data.bidType ?? "",
    bostonType: data.bostonType ?? "",
    locationName: data.locationName ?? "",
    notes: data.notes ?? "",
    playedAt: playedAt?.toISOString() ?? "",
    playedDate: playedAt ? playedAt.toISOString().slice(0, 10) : "",
    playedTime: playedAt ? `${String(playedAt.getHours()).padStart(2, "0")}:${String(playedAt.getMinutes()).padStart(2, "0")}` : "",
    summary: `${teamLabel(data.winningTeam)} Bostoned ${teamLabel(data.losingTeam)}`
  };
}


function getSessionCached<T>(key: string, ttlMs: number): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.timestamp === 'number' && Date.now() - parsed.timestamp < ttlMs) {
      return parsed.data as T;
    }
  } catch (_) {}
  return null;
}

function setSessionCached<T>(key: string, data: T): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (_) {}
}

function removeSessionCached(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(key);
  } catch (_) {}
}

function getLocalCached<T>(key: string, ttlMs: number): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.timestamp === 'number' && Date.now() - parsed.timestamp < ttlMs) {
      return parsed.data as T;
    }
  } catch (_) {}
  return null;
}

function setLocalCached<T>(key: string, data: T): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (_) {}
}

function removeLocalCached(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch (_) {}
}

let cachedMembers: { data: UserProfile[]; timestamp: number } | null = null;
let inflightMembersPromise: Promise<UserProfile[]> | null = null;
const MEMBERS_CACHE_TTL_MS = 900_000; // 15 minutes

export function invalidateMembersCache() {
  cachedMembers = null;
  inflightMembersPromise = null;
  removeSessionCached('acs_cache_members');
}

export function getCachedMemberById(uid: string): UserProfile | null {
  if (!uid) return null;
  const list = cachedMembers?.data || getSessionCached<UserProfile[]>('acs_cache_members', MEMBERS_CACHE_TTL_MS);
  if (!list || !Array.isArray(list)) return null;
  return list.find(m => m.id === uid || (m as any).uid === uid) || null;
}

export function resolveDisplayName(profile?: Partial<UserProfile> | null, user?: any): string {
  if (profile?.displayName?.trim()) return profile.displayName.trim();
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  if (profile?.nickname?.trim()) return profile.nickname.trim();
  if (user?.displayName?.trim()) return user.displayName.trim();
  return 'Card Shark';
}

export async function getMembers(forceRefresh = false): Promise<UserProfile[]> {
  const now = Date.now();
  if (forceRefresh) {
    cachedMembers = null;
    inflightMembersPromise = null;
    removeSessionCached('acs_cache_members');
    removeLocalCached('acs_cache_members');
  } else {
    if (cachedMembers && (now - cachedMembers.timestamp < MEMBERS_CACHE_TTL_MS)) {
      return cachedMembers.data;
    }
    const sessionData = getSessionCached<UserProfile[]>('acs_cache_members', MEMBERS_CACHE_TTL_MS);
    if (sessionData) {
      cachedMembers = { data: sessionData, timestamp: now };
      return sessionData;
    }
    const localData = getLocalCached<UserProfile[]>('acs_cache_members', 24 * 60 * 60 * 1000);
    if (localData && localData.length > 0) {
      cachedMembers = { data: localData, timestamp: now };
      if (!inflightMembersPromise) {
        setTimeout(() => {
          getMembers(true).catch(() => {});
        }, 100);
      }
      return localData;
    }
  }

  if (inflightMembersPromise) {
    return inflightMembersPromise;
  }

  inflightMembersPromise = (async () => {
    try {
      const response = await apiFetch('/api/members');
      if (response.ok) {
        const pgData = await response.json();
        const result = pgData.map((m: any) => ({
          ...m,
          photoUrl: fixPhotoUrl(m.photoUrl),
          avatarUrl: fixPhotoUrl(m.avatarUrl || m.photoUrl)
        })).sort((a: any, b: any) => (a.displayName || "").localeCompare(b.displayName || ""));
        cachedMembers = { data: result, timestamp: Date.now() };
        setSessionCached('acs_cache_members', result);
        setLocalCached('acs_cache_members', result);
        return result;
      } else {
        console.warn("[getMembers] PostgreSQL endpoint returned status:", response.status);
        return cachedMembers?.data || [];
      }
    } catch (e) {
      console.warn("[getMembers] Failed to load members from PostgreSQL:", e);
      return cachedMembers?.data || [];
    } finally {
      inflightMembersPromise = null;
    }
  })();

  return inflightMembersPromise;
}

export const MEMBER_ID_ALIASES: Record<string, string> = {
  "h0sYaBLfkt51tB7gZh4x": "ri6fEemOajRPhM5Vwylz4q8YS7o1", // Joyce
  "BPSazM9l3Vw1nD6Y4PsB": "Qc7GhnaKzQauyGdcDQIE6VCJqV62"  // Cecilia
};

export async function getMemberProfile(uid: string = auth.currentUser?.uid || ""): Promise<UserProfile | null> {
  if (!uid) return null;
  const canonicalUid = MEMBER_ID_ALIASES[uid.trim()] || uid.trim();
  // Check cached members list first (instant 0ms resolution)
  if (cachedMembers?.data) {
    const found = cachedMembers.data.find(m => m.id === canonicalUid);
    if (found && (found.photoUrl || found.avatarUrl)) return found;
  }
  try {
    const res = await apiFetch(`/api/members/${encodeURIComponent(canonicalUid)}`);
    if (res.ok) {
      const m = await res.json();
      const profile: UserProfile = {
        ...m,
        photoUrl: fixPhotoUrl(m.photoUrl),
        avatarUrl: fixPhotoUrl(m.avatarUrl || m.photoUrl)
      };
      if (!profile.photoUrl) {
        try {
          const snap = await getDoc(doc(db, "members", canonicalUid));
          if (snap.exists()) {
            const fsData = snap.data();
            const fsPhoto = fsData.photoUrl || fsData.avatarUrl;
            if (fsPhoto) {
              profile.photoUrl = fixPhotoUrl(fsPhoto);
              profile.avatarUrl = profile.photoUrl;
            }
          }
        } catch (_) {}
      }
      return profile;
    }
  } catch (err) {
    console.warn("[getMemberProfile] Failed to fetch member from PostgreSQL:", err);
  }

  // Graceful fallback to Firestore during migration
  try {
    const snap = await getDoc(doc(db, "members", canonicalUid));
    if (snap.exists()) {
      return normalizeMember(canonicalUid, snap.data());
    }
  } catch (_) {}

  return null;
}
export function isProfileComplete(profile: UserProfile | null): boolean {
  return !!(profile?.nickname && profile?.phone && profile?.city);
}

export interface GalleryAlbum {
  id: string;
  title: string;
  url: string;
  description: string;
}

export async function getGalleryAlbums(): Promise<GalleryAlbum[]> {
  try {
    const settingsSnap = await getDoc(doc(db, "settings", "club"));
    if (settingsSnap.exists()) {
      const data = settingsSnap.data();
      if (Array.isArray(data.albums) && data.albums.length > 0) {
        return data.albums;
      }
      if (data.galleryAlbumUrl) {
        return [{
          id: "club-gallery",
          title: "Club Gallery",
          url: data.galleryAlbumUrl,
          description: "Photos from our shared album."
        }];
      }
    }
  } catch (error) {
    console.error("Error in getGalleryAlbums:", error);
  }
  
  return [
    {
      id: "tournament-highlights",
      title: "Tournament Highlights",
      url: "https://photos.app.goo.gl/BcFQe7PgBG1Jm9dz5",
      description: "Card Sharks weekly showdowns and championship highlights."
    }
  ];
}

export interface AlbumPhoto {
  uid: string;
  url: string;
  width: number;
  height: number;
}

export async function getPhotosForAlbum(url: string, albumId?: string): Promise<AlbumPhoto[]> {
  if (albumId) {
    try {
      const cacheRef = doc(db, "gallery_caches", albumId);
      const cacheSnap = await getDoc(cacheRef);
      if (cacheSnap.exists()) {
        const cacheData = cacheSnap.data();
        const lastSynced = parseDate(cacheData.lastSynced);
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        if (lastSynced && lastSynced > oneDayAgo && Array.isArray(cacheData.urls) && cacheData.urls.length > 0) {
          console.log(`[getPhotosForAlbum] Serving client-side Firestore cache for album: ${albumId}`);
          return cacheData.urls;
        }
      }
    } catch (cacheError) {
      console.warn("[getPhotosForAlbum] Error reading client-side cache, falling back to function:", cacheError);
    }
  }

  try {
    const getPhotosFn = httpsCallable<any, { success: boolean; urls: AlbumPhoto[] }>(functions, "getPhotosFromSharedAlbum");
    const response = await getPhotosFn({ url, albumId });
    if (response.data?.success && Array.isArray(response.data.urls)) {
      return response.data.urls;
    }
    return [];
  } catch (error) {
    console.error("Error in getPhotosForAlbum:", error);
    return [];
  }
}

let cachedUpcomingEvents: { data: Event[]; timestamp: number } | null = null;
let inflightUpcomingEventsPromise: Promise<Event[]> | null = null;
const UPCOMING_EVENTS_CACHE_TTL_MS = 60_000; // 1 minute
const UPCOMING_EVENTS_PERSIST_TTL_MS = 5 * 60 * 1000; // 5 minutes persistent local cache for instant load

export function invalidateUpcomingEventsCache() {
  cachedUpcomingEvents = null;
  inflightUpcomingEventsPromise = null;
  removeSessionCached('acs_cache_upcoming_events');
  removeLocalCached('acs_cache_upcoming_events');
}

export async function getUpcomingEvents(forceRefresh = false): Promise<Event[]> {
  const now = Date.now();
  if (forceRefresh) {
    cachedUpcomingEvents = null;
    inflightUpcomingEventsPromise = null;
    removeSessionCached('acs_cache_upcoming_events');
    removeLocalCached('acs_cache_upcoming_events');
  } else {
    if (cachedUpcomingEvents && (now - cachedUpcomingEvents.timestamp < UPCOMING_EVENTS_CACHE_TTL_MS)) {
      return cachedUpcomingEvents.data;
    }
    const sessionData = getSessionCached<Event[]>('acs_cache_upcoming_events', UPCOMING_EVENTS_CACHE_TTL_MS);
    if (sessionData) {
      const cleanSessionData = sessionData.filter(e => e && e.title && e.title.trim() !== '' && e.title.trim().toLowerCase() !== 'untitled event');
      if (cleanSessionData.length > 0) {
        cachedUpcomingEvents = { data: cleanSessionData, timestamp: now };
        return cleanSessionData;
      }
    }
    const localData = getLocalCached<Event[]>('acs_cache_upcoming_events', UPCOMING_EVENTS_PERSIST_TTL_MS);
    if (localData && localData.length > 0) {
      const cleanLocalData = localData.filter(e => e && e.title && e.title.trim() !== '' && e.title.trim().toLowerCase() !== 'untitled event');
      if (cleanLocalData.length > 0) {
        cachedUpcomingEvents = { data: cleanLocalData, timestamp: now };
        // Always trigger background refresh when using local cache
        if (!inflightUpcomingEventsPromise) {
          setTimeout(() => {
            getUpcomingEvents(true).catch(() => {});
          }, 100);
        }
        return cleanLocalData;
      }
    }
  }

  if (inflightUpcomingEventsPromise) {
    return inflightUpcomingEventsPromise;
  }

  inflightUpcomingEventsPromise = (async () => {
    try {
      const currentMemberId = auth.currentUser?.uid ?? null;

      // 1. Try PostgreSQL API with an AbortController timeout to prevent UI freezes
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timeoutId = controller ? setTimeout(() => controller.abort(), 4000) : null;

      try {
        const response = await apiFetch('/api/events', {
          signal: controller?.signal
        });
        if (timeoutId) clearTimeout(timeoutId);

        if (response.ok) {
          const pgEvents = await response.json();
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

          const validPgEvents = (Array.isArray(pgEvents) ? pgEvents : []).filter((e: any) => e && e.title && e.title.trim() !== '' && e.is_deleted !== true);

          const result: Event[] = validPgEvents.map((e: any) => {
            const isUpcoming = e.isUpcoming !== undefined 
              ? Boolean(e.isUpcoming) 
              : (typeof e.date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(e.date) 
                  ? e.date.substring(0, 10) >= todayStr 
                  : (e.startDateTime ? new Date(e.startDateTime) >= today : true));

            let sortDate = e.sortDate;
            if (!sortDate && e.date) {
              const timePart = e.startTime ? (e.startTime.length === 5 ? `${e.startTime}:00` : e.startTime) : '18:00:00';
              sortDate = `${e.date}T${timePart}Z`;
            } else if (!sortDate && e.startDateTime) {
              sortDate = new Date(e.startDateTime).toISOString();
            }

            return {
              ...e,
              isUpcoming,
              sortDate,
              venuePhotoUrl: fixPhotoUrl(e.venuePhotoUrl),
              photoUrls: Array.isArray(e.photoUrls) ? e.photoUrls.map(fixPhotoUrl) : [],
              rsvpStatus: currentMemberId 
                ? (e.goingAttendees?.some((a: any) => a.memberId === currentMemberId) ? "going" 
                  : e.maybeAttendees?.some((a: any) => a.memberId === currentMemberId) ? "maybe" : "") 
                : ""
            };
          }).sort((a: any, b: any) => {
            // 1. Featured first
            if (a.isFeatured && !b.isFeatured) return -1;
            if (!a.isFeatured && b.isFeatured) return 1;

            // 2. Upcoming events next
            const isUpcomingA = a.isUpcoming !== false;
            const isUpcomingB = b.isUpcoming !== false;
            if (isUpcomingA && !isUpcomingB) return -1;
            if (!isUpcomingA && isUpcomingB) return 1;

            // 3. Chronological sorting
            const da = a.sortDate || a.date || "9999";
            const db = b.sortDate || b.date || "9999";
            return da.localeCompare(db);
          });

          cachedUpcomingEvents = { data: result, timestamp: Date.now() };
          setSessionCached('acs_cache_upcoming_events', result);
          setLocalCached('acs_cache_upcoming_events', result);
          return result;
        }
      } catch (err) {
        if (timeoutId) clearTimeout(timeoutId);
        console.warn("[getUpcomingEvents] PostgreSQL endpoint unreachable or timed out, falling back to Firestore:", err);
      }

      // If PostgreSQL query failed or was empty, return cached events
      return cachedUpcomingEvents?.data || [];
    } catch (e) {
      console.warn("[getUpcomingEvents] Failed to load upcoming events:", e);
      return cachedUpcomingEvents?.data || [];
    } finally {
      inflightUpcomingEventsPromise = null;
    }
  })();

  return inflightUpcomingEventsPromise;
}

export async function getEventDetails(eventId: string): Promise<Event | null> {
    if (!eventId) return null;

    // Check cached upcoming events first as fallback baseline
    const cachedFallback = cachedUpcomingEvents?.data?.find(e => e.id === eventId);

    // 1. Try PostgreSQL API with an AbortController timeout to get authoritative backend data
    let pgEvent: any = null;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 3500) : null;
    try {
        const response = await apiFetch(`/api/events/${encodeURIComponent(eventId)}`, {
            signal: controller?.signal
        });
        if (timeoutId) clearTimeout(timeoutId);
        if (response.ok) {
            const parsed = await response.json();
            if (parsed && parsed.id) {
                pgEvent = parsed;
            }
        }
    } catch {
        if (timeoutId) clearTimeout(timeoutId);
    }

    // Read comments subcollection (for member comments/discussion)
    const commentsSnap = await getDocs(
        query(collection(db, "events", eventId, "comments"), orderBy("createdAt", "asc"))
    ).catch(() => ({ docs: [] } as any));

    if (!pgEvent) {
        return cachedFallback || null;
    }
    const data = pgEvent;

    // Comments from the event comments subcollection
    const commentsRaw = commentsSnap.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));

    const rsvps = data.rsvps ?? {};
    const hostIds = Array.isArray(data.hostMemberIds) ? data.hostMemberIds : [];
    const memberIds = [...new Set([
        ...Object.keys(rsvps), 
        ...(data.goingAttendees || []).map((a: any) => a.memberId),
        ...commentsRaw.map((c: any) => c.memberId).filter(Boolean),
        ...hostIds
    ])];

    const profiles: Record<string, UserProfile> = {};
    if (auth.currentUser && memberIds.length > 0) {
        try {
            // Use cached members if available, or fetch with 3.5s timeout so UI never hangs
            let allMembers = cachedMembers?.data;
            if (!allMembers || allMembers.length === 0) {
                allMembers = await Promise.race([
                    getMembers(),
                    new Promise<UserProfile[]>((_, reject) => setTimeout(() => reject(new Error('timeout')), 3500))
                ]).catch(() => cachedMembers?.data || []);
            }
            const memberLookup = new Map(allMembers.map(m => [m.id, m]));
            memberIds.forEach(mId => {
                const found = memberLookup.get(mId);
                if (found) profiles[mId] = found;
            });
        } catch (err) {
            console.warn("Failed to fetch member details for event:", err);
        }
    }

    const [venueSnap, packageSnap] = await Promise.all([
        data.venue ? Promise.resolve(null) : (data.venueId ? getDoc(doc(db, "venues", data.venueId)).catch(() => null) : Promise.resolve(null)),
        data.pointsPackage ? Promise.resolve(null) : (data.pointsPackageId ? getDoc(doc(db, "points_packages", data.pointsPackageId)).catch(() => null) : Promise.resolve(null))
    ]);

    const normalized = normalizeEvent(eventId, data, auth.currentUser?.uid, profiles);

    if (data.venue) {
        normalized.venue = data.venue;
    } else if (venueSnap && venueSnap.exists()) {
        normalized.venue = { id: venueSnap.id, name: venueSnap.data().name, address: venueSnap.data().address, photoUrl: fixPhotoUrl(venueSnap.data().photoUrl) };
    } else {
        normalized.venue = { id: data.venueId || "", name: data.venueName || "Venue", address: data.venueAddress || "", photoUrl: fixPhotoUrl(data.venuePhotoUrl) };
    }
    
    normalized.venueName = data.venueName || normalized.venue?.name || "";
    normalized.venueAddress = data.venueAddress || normalized.venue?.address || "";
    normalized.venuePhotoUrl = fixPhotoUrl(data.venuePhotoUrl || normalized.venue?.photoUrl || "");

    const venueImg = normalized.venuePhotoUrl;
    if (venueImg && !normalized.photoUrls.includes(venueImg)) {
        normalized.photoUrls.push(venueImg);
    }

    normalized.pointsPackage = data.pointsPackage || (packageSnap && packageSnap.exists()
        ? { id: packageSnap.id, ...packageSnap.data() } as any
        : null);

    const comments: EventComment[] = commentsRaw
        .filter((c: any) => {
            const p = profiles[c.memberId];
            return !p?.isBanned;
        })
        .map((c: any) => {
            const p = profiles[c.memberId];
            const cDate = parseDate(c.createdAt) || new Date();
            return {
                id: c.id, memberId: c.memberId,
                member: p || null,
                displayName: p?.displayName || c.displayName || "Member",
                avatarUrl: fixPhotoUrl(p?.photoUrl || c.avatarUrl),
                text: c.text, parentCommentId: c.parentCommentId,
                createdAt: cDate.toISOString(), timeAgo: formatRelativeTime(cDate)
            };
        });

    return { ...normalized, comments };
}

export async function updateEventRsvp(eventId: string, status: string): Promise<{ success: boolean; message?: string }> {
    if (!auth.currentUser) return { success: false, message: "Not signed in." };
    const p = await getMemberProfile(auth.currentUser.uid);
    if (!p) return { success: false, message: "Profile not found." };
    try {
        // Authoritative RSVP write directly to PostgreSQL
        const response = await apiFetch('/api/events/' + encodeURIComponent(eventId) + '/rsvp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                memberId: auth.currentUser.uid,
                status,
                displayName: p.displayName,
                avatarUrl: p.photoUrl
            })
        });

        if (!response.ok) {
            console.warn("[updateEventRsvp] PostgreSQL RSVP endpoint returned:", response.status);
        }

        await writeAuditLog("Update Event RSVP", eventId, "Status: " + status);
        invalidateUpcomingEventsCache();

        // Announce the RSVP in activityFeed and preAggregatedFeed
        try {
            const cachedEvent = cachedUpcomingEvents?.data?.find(e => e.id === eventId);
            const eventTitle = cachedEvent?.title || "Card Sharks Event";

            await writeFeedItem({
                type: "rsvp",
                actorId: auth.currentUser.uid,
                actorDisplayName: p.displayName,
                actorAvatarUrl: p.photoUrl,
                targetId: eventId,
                body: "RSVP'ed " + status + " to " + eventTitle
            });

            await logPreAggregatedActivity("rsvp", eventId, eventTitle, "");
        } catch (feedErr) {
            console.warn("Failed to log RSVP feed item:", feedErr);
        }

        return { success: true };
    } catch (e: any) { return { success: false, message: e.message }; }
}

export async function addEventComment(eventId: string, text: string, parentCommentId = ""): Promise<{ success: boolean; message?: string }> {
    if (!auth.currentUser) return { success: false, message: "Not signed in." };
    const p = await getMemberProfile(auth.currentUser.uid);
    if (!p) return { success: false, message: "Profile not found." };
    const comment = { memberId: auth.currentUser.uid, text, parentCommentId, createdAt: new Date().toISOString() };
    try {
        await addDoc(collection(db, "events", eventId, "comments"), comment);
        if (!parentCommentId) {
            await writeFeedItem({ 
                type: "comment", 
                actorId: auth.currentUser.uid, 
                actorDisplayName: p.displayName, 
                actorAvatarUrl: p.photoUrl, 
                targetId: eventId, 
                body: `commented on an event`, 
                metadata: { commentText: text.substring(0, 100) } 
            });
        }

        // Dispatch @mention notifications if members were tagged
        import("./services/notificationCenterService").then(async ({ extractMentionedMembers, dispatchMentionNotifications }) => {
            try {
                const allMembers = await getMembers();
                const mentioned = extractMentionedMembers(text, allMembers);
                if (mentioned.length > 0 && auth.currentUser) {
                    await dispatchMentionNotifications({
                        actor: {
                            id: auth.currentUser.uid,
                            displayName: p.displayName || p.nickname || 'A member',
                            photoUrl: p.photoUrl || ''
                        },
                        text,
                        snippet: text,
                        type: 'event_comment_mention',
                        targetType: 'event',
                        targetId: eventId,
                        targetUrl: `/events/${eventId}`,
                        mentionedMembers: mentioned
                    });
                }
            } catch (err) {
                console.warn("[addEventComment] Failed to dispatch mention notifications:", err);
            }
        }).catch(() => {});

        return { success: true };
    } catch (e: any) { return { success: false, message: e.message }; }
}

export async function getSocialActivityFeed(followingOnly = false, memberId: string | null = null): Promise<ActivityItem[]> {
  if (!auth.currentUser && !memberId) return [];
  try {
    const qBase = collection(db, "activityFeed");
    let docs: QueryDocumentSnapshot<DocumentData>[] = [];
    if (memberId) {
      // Filter for a specific member
      const snap = await getDocs(query(qBase, where("actorId", "==", memberId), orderBy("createdAt", "desc"), limit(50)));
      docs = snap.docs;
    } else if (!followingOnly) {
      const snap = await getDocs(query(qBase, orderBy("createdAt", "desc"), limit(50)));
      docs = snap.docs;
    } else {
      const fSnap = await getDocs(query(collection(db, "follows"), where("followerId", "==", auth.currentUser!.uid)));
      const ids = fSnap.docs.map(d => d.data().followeeId).filter(Boolean);
      if (!ids.includes(auth.currentUser!.uid)) ids.push(auth.currentUser!.uid);
      const results: QueryDocumentSnapshot<DocumentData>[] = [];
      const idsToProcess = Array.isArray(ids) ? ids : [];
      for (let i = 0; i < idsToProcess.length; i += 30) {
        const chunk = idsToProcess.slice(i, i + 30);
        if (chunk.length === 0) continue;
        const snap = await getDocs(query(qBase, where("actorId", "in", chunk), orderBy("createdAt", "desc"), limit(50)));
        results.push(...snap.docs);
      }
      docs = results;
    }

    const normalizedFeed = docs
      .map(d => normalizeFeedDoc(d))
      .filter(item => {
        const t = (item.type || '').toLowerCase();
        if (t === 'admin_post' || t.includes('admin')) return true;
        if (t.includes('rsvp') || t.includes('event')) return true;
        if (t.includes('follow')) return true;
        if (t.includes('join')) return true;
        if (t.includes('gallery') || t.includes('photo')) return true;
        if (t.includes('comment')) return true;
        return false;
      });

    // Batch resolve actor member profiles relationally
    const actorIds = [...new Set(normalizedFeed.map(item => item.actorId).filter(Boolean))];
    const targetIds = [...new Set(normalizedFeed.map(item => item.targetId).filter(Boolean))];

    const actorProfiles: Record<string, UserProfile> = {};
    if (actorIds.length > 0) {
      try {
        const allMembers = await getMembers();
        const memberLookup = new Map(allMembers.map(m => [m.id, m]));
        actorIds.forEach(id => {
          const found = memberLookup.get(id);
          if (found) actorProfiles[id] = found;
        });
      } catch (err) {
        console.warn("Failed to fetch actor member details for feed:", err);
      }
    }

    const targetEvents: Record<string, any> = {};
    if (targetIds.length > 0) {
      const validTargetIds = targetIds.filter((id): id is string => !!id);
      const snaps = await Promise.all(validTargetIds.map(id => getDoc(doc(db, "events", id))));
      snaps.forEach((s, idx) => {
        if (s.exists()) {
          targetEvents[validTargetIds[idx]] = s.data();
        }
      });
    }

    return normalizedFeed.map(item => {
      const actor = actorProfiles[item.actorId];
      const event = item.targetId ? targetEvents[item.targetId] : null;
      return {
        ...item,
        actor: actor || null,
        actorDisplayName: actor?.displayName || item.actorDisplayName,
        actorAvatarUrl: actor?.photoUrl || item.actorAvatarUrl,
        target: event || null,
        targetTitle: event?.title || item.targetTitle
      } as any;
    }).sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "")).slice(0, 50);
  } catch (e) { return []; }
}

export async function getFeedItemLikes(feedItemId: string, fallbackCount?: number): Promise<{ count: number; likedByMe: boolean }> {
    const user = auth.currentUser;
    let likedByMe = false;

    if (user) {
      try {
        const myLikeDoc = await getDoc(doc(db, 'feedLikes', `${feedItemId}_${user.uid}`));
        likedByMe = myLikeDoc.exists();
      } catch {}
    }

    if (typeof fallbackCount === 'number') {
      return { count: fallbackCount, likedByMe };
    }

    try {
      const q = query(collection(db, 'feedLikes'), where('feedItemId', '==', feedItemId));
      const snap = await getDocs(q);
      return { count: snap.size, likedByMe: snap.docs.some(d => d.data().userId === user?.uid) };
    } catch {
      return { count: 0, likedByMe };
    }
}

export async function toggleFeedItemLike(feedItemId: string): Promise<{ success: boolean; liked: boolean; count: number }> {
    const user = auth.currentUser;
    if (!user) return { success: false, liked: false, count: 0 };
    const docRef = doc(db, 'feedLikes', `${feedItemId}_${user.uid}`);
    const existing = await getDoc(docRef);
    const feedItemRef = doc(db, 'activityFeed', feedItemId);

    if (existing.exists()) { 
        await deleteDoc(docRef); 
        try {
            await updateDoc(feedItemRef, { likesCount: increment(-1) });
        } catch (err) {
            console.warn("Failed to decrement likesCount", err);
        }
    } else { 
        await setDoc(docRef, { feedItemId, userId: user.uid, likedAt: new Date().toISOString() }); 
        try {
            await updateDoc(feedItemRef, { likesCount: increment(1) });
        } catch (err) {
            console.warn("Failed to increment likesCount", err);
        }
    }
    const likes = await getFeedItemLikes(feedItemId);
    return { success: true, liked: !existing.exists(), count: likes.count };
}

export async function logPreAggregatedActivity(type: string, targetId: string, targetName: string, targetImageUrl: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;
  try {
    const p = await getMemberProfile(user.uid);
    const userName = p?.nickname || p?.displayName || `${p?.firstName || ''} ${p?.lastName || ''}`.trim() || "Member";
    const avatarUrl = p?.photoUrl || "";

    const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '_'); // yyyy_MM_dd
    
    let boundId = targetId;
    if (type === "gallery" || type === "follow" || type === "memberfollow") {
      boundId = user.uid;
    }

    // Check today, yesterday, and 2 days ago for an existing doc within 48h
    const now = new Date();
    const d0 = now.toISOString().split('T')[0].replace(/-/g, '_');
    const d1 = new Date(now.getTime() - 86400000).toISOString().split('T')[0].replace(/-/g, '_');
    const d2 = new Date(now.getTime() - 172800000).toISOString().split('T')[0].replace(/-/g, '_');

    let targetDocRef = doc(db, "preAggregatedFeed", `${type}_${boundId}_${d0}`);
    for (const d of [d0, d1, d2]) {
      const candidateRef = doc(db, "preAggregatedFeed", `${type}_${boundId}_${d}`);
      const candidateSnap = await getDoc(candidateRef);
      if (candidateSnap.exists()) {
        const cData = candidateSnap.data();
        const latestMs = cData.latestTimestamp?.toMillis?.() || 0;
        if (!cData.isDeleted && Math.abs(Date.now() - latestMs) <= 48 * 3600 * 1000) {
          targetDocRef = candidateRef;
          break;
        }
      }
    }
    const docRef = targetDocRef;
    
    const baseWeight = type === "rsvp" ? 30.0 : (type === "join" ? 20.0 : 15.0);

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(docRef);
      const actor = { id: user.uid, name: userName, avatarUrl: avatarUrl };
      
      const formatHeadline = (actors: any[]) => {
        let actorText = "";
        const getName = (a: any) => (a?.name && a.name.trim() !== '') ? a.name : "Member";
        if (actors.length === 0) {
          actorText = "Someone";
        } else if (actors.length === 1) {
          actorText = `<b>${getName(actors[0])}</b>`;
        } else if (actors.length === 2) {
          actorText = `<b>${getName(actors[0])}</b> and <b>${getName(actors[1])}</b>`;
        } else {
          actorText = `<b>${getName(actors[0])}</b> and <b>${actors.length - 1} others</b>`;
        }
        
        if (type === "join") return `${actorText} joined <b>${targetName}</b>`;
        if (type === "rsvp") return `${actorText} RSVP'ed to <b>${targetName}</b>`;
        if (type === "gallery") return `${actorText} updated their photo gallery`;
        return `${actorText} interacted with <b>${targetName}</b>`;
      };
      
      const formatSubheader = (count: number) => {
        return "";
      };

      if (!snap.exists()) {
        const actors = [actor];
        transaction.set(docRef, {
          activityType: type,
          displayType: "Compact",
          latestTimestamp: Timestamp.now(),
          importanceScore: baseWeight,
          headline: formatHeadline(actors),
          subheader: formatSubheader(1),
          targetId: targetId,
          targetName: targetName,
          targetImageUrl: (type === "join" && avatarUrl) ? avatarUrl : (type === "rsvp" ? "" : (targetImageUrl || "")),
          aggregatedCount: 1,
          primaryActors: actors
        });
      } else {
        const existing = snap.data() as PreAggregatedFeedItem;
        const newCount = (existing.aggregatedCount || 1) + 1;
        
        let actors = existing.primaryActors || [];
        if (!actors.some(a => a.id === user.uid)) {
          actors = [...actors, actor];
        }
        
        const score = baseWeight * newCount;
        let displayType = newCount > 1 ? "Grouped" : "Compact";
        if (score > 40.0) {
          displayType = "Featured";
        }

        let updatedImageUrl = existing.targetImageUrl || "";
        if (type === "join") {
          const images = (existing.targetImageUrl || "").split(',').filter(Boolean);
          if (avatarUrl && !images.includes(avatarUrl)) {
            images.push(avatarUrl);
          }
          updatedImageUrl = images.join(',');
        } else if (type === "gallery") {
          updatedImageUrl = targetImageUrl || avatarUrl;
        } else if (type === "rsvp") {
          updatedImageUrl = "";
        }
        
        transaction.update(docRef, {
          aggregatedCount: newCount,
          latestTimestamp: Timestamp.now(),
          headline: formatHeadline(actors),
          subheader: formatSubheader(newCount),
          importanceScore: score,
          displayType: displayType,
          primaryActors: actors,
          targetImageUrl: updatedImageUrl
        });
      }
    });
  } catch (e) {
    console.warn("[logPreAggregatedActivity] Error:", e);
  }
}

export async function getFeedItemComments(feedItemId: string): Promise<any[]> {
  try {
    const q = query(collection(db, "preAggregatedFeed", feedItemId, "comments"), orderBy("createdAt", "asc"));
    const snap = await getDocs(q);
    const commentsRaw = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    const memberIds = [...new Set(commentsRaw.map((c: any) => c.memberId).filter(Boolean))];
    const membersMap: Record<string, UserProfile> = {};
    if (memberIds.length > 0) {
      try {
        const allMembers = await getMembers();
        const memberLookup = new Map(allMembers.map(m => [m.id, m]));
        memberIds.forEach(id => {
          const found = memberLookup.get(id);
          if (found) membersMap[id] = found;
        });
      } catch (err) {
        console.warn("Failed to fetch member details for feed comments:", err);
      }
    }
    
    return commentsRaw.map((c: any) => ({
      ...c,
      actorDisplayName: membersMap[c.memberId]?.displayName || "Member",
      actorAvatarUrl: membersMap[c.memberId]?.photoUrl || ""
    }));
  } catch (e) {
    console.error("[getFeedItemComments] Error:", e);
    return [];
  }
}

export async function addFeedItemComment(feedItemId: string, text: string): Promise<{ success: boolean; message?: string }> {
  if (!auth.currentUser) return { success: false, message: "Not signed in." };
  try {
    const user = auth.currentUser;
    const comment = {
      memberId: user.uid,
      text,
      createdAt: new Date().toISOString()
    };
    await addDoc(collection(db, "preAggregatedFeed", feedItemId, "comments"), comment);
    
    try {
      await updateDoc(doc(db, "preAggregatedFeed", feedItemId), { commentsCount: increment(1) });
    } catch (err) {
      console.warn("Failed to increment commentsCount on preAggregatedFeed doc", err);
    }
    
    // Dispatch @mention notifications if members were tagged
    import("./services/notificationCenterService").then(async ({ extractMentionedMembers, dispatchMentionNotifications }) => {
      try {
        const allMembers = await getMembers();
        const mentioned = extractMentionedMembers(text, allMembers);
        if (mentioned.length > 0 && user) {
          const actorProfile = await getMemberProfile(user.uid);
          await dispatchMentionNotifications({
            actor: {
              id: user.uid,
              displayName: actorProfile?.displayName || actorProfile?.nickname || 'A member',
              photoUrl: actorProfile?.photoUrl || ''
            },
            text,
            snippet: text,
            type: 'mention_comment',
            targetType: 'post',
            targetId: feedItemId,
            targetUrl: '/feed',
            mentionedMembers: mentioned
          });
        }
      } catch (err) {
        console.warn("[addFeedItemComment] Failed to dispatch mention notifications:", err);
      }
    }).catch(() => {});

    return { success: true };
  } catch (e: any) {
    return { success: false, message: e.message };
  }
}

export async function getPublicProfile(memberId: string): Promise<UserProfile | null> {
  const canonicalId = (memberId && MEMBER_ID_ALIASES[memberId.trim()]) || memberId;
  const p = await getMemberProfile(canonicalId);
  if (!p || !auth.currentUser) return p;
  const fSnap = await getDoc(doc(db, "follows", `${auth.currentUser.uid}_${canonicalId}`));
  return { ...p, isFollowing: fSnap.exists(), isOwnProfile: canonicalId === auth.currentUser.uid } as any;
}

export async function savePublicBio(bio: string): Promise<{ success: boolean; message?: string }> {
  if (!auth.currentUser) return { success: false, message: "Not signed in." };
  try {
    const trimmedBio = (bio ?? "").substring(0, 300);
    await updateDoc(doc(db, "members", auth.currentUser.uid), { bio: trimmedBio, updatedAt: new Date() });
    try {
      await apiFetch(`/api/members/${encodeURIComponent(auth.currentUser.uid)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bio: trimmedBio })
      });
    } catch (pgErr) {
      console.warn("[savePublicBio] Failed to sync to PostgreSQL:", pgErr);
    }
    return { success: true, message: "Bio saved successfully." };
  } catch (e: any) {
    return { success: false, message: e.message };
  }
}

export async function uploadGalleryPhoto(index: number, fileName: string, contentType: string, dataUrl: string): Promise<{ success: boolean; message?: string }> {
  if (!auth.currentUser) return { success: false };
  const compressed = await compressImage(dataUrl);
  const photoUrl = await uploadDataUrl(`gallery-photos/${auth.currentUser.uid}/slot-${index}`, compressed);
  return { success: true, message: photoUrl };
}

export async function deleteGalleryPhoto(index: number): Promise<{ success: boolean }> {
  return { success: true };
}

export async function saveGalleryPhotoUrls(urls: string[]): Promise<{ success: boolean }> {
  if (!auth.currentUser) return { success: false };
  const activeUrls = (urls || []).slice(0, 3);

  try {
    await updateDoc(doc(db, "members", auth.currentUser.uid), { 
      galleryPhotoUrls: activeUrls, 
      updatedAt: new Date() 
    });
  } catch (err) {
    console.warn("Failed to update local member document:", err);
  }

  try {
    const token = await getCurrentIdToken();
    if (token) {
      const response = await apiFetch('/api/profiles/me/gallery', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          PhotoUrls: activeUrls,
          photoUrls: activeUrls
        })
      });
      if (!response.ok) {
        console.warn("[saveGalleryPhotoUrls] Backend API response not ok:", response.statusText);
      }
    }
  } catch (apiErr) {
    console.warn("[saveGalleryPhotoUrls] Backend API call failed:", apiErr);
  }

  return { success: true };
}

export async function uploadProfilePhoto(fileName: string, contentType: string, dataUrl: string): Promise<{ success: boolean; message?: string }> {
  if (!auth.currentUser) return { success: false };
  const uid = auth.currentUser.uid;

  // Retrieve current display name and old photo before updating
  const original: any = (await getMemberProfile(uid)) || {};
  const currentName = original.displayName || "";
  const oldPhoto = original.photoUrl || "";

  const compressed = await compressImage(dataUrl);
  const photoUrl = await uploadDataUrl(`profile-photos/${uid}/profile-photo`, compressed);
  await updateDoc(doc(db, "members", uid), { photoUrl, avatarUrl: photoUrl, updatedAt: new Date() });

  try {
    await apiFetch(`/api/members/${encodeURIComponent(uid)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photoUrl, avatarUrl: photoUrl })
    });
  } catch (pgErr) {
    console.warn("[uploadProfilePhoto] Failed to sync to PostgreSQL:", pgErr);
  }

  await propagateMemberUpdates(uid, currentName, photoUrl, currentName, oldPhoto);
  invalidateMembersCache();

  return { success: true, message: photoUrl };
}

// --- Chat Service ---

export function subscribeToMessageThreads(callback: (threads: MessageThread[]) => void): () => void {
  if (!auth.currentUser) return () => {};
  const q = query(collection(db, "messageThreads"), where("memberIds", "array-contains", auth.currentUser.uid));
  return onSnapshot(q, async (snapshot) => {
    const threads = snapshot.docs.map(doc => normalizeThread(doc.id, doc.data()));
    const allMemberIds = [...new Set(threads.flatMap(t => t.memberIds))];

    const membersMap: Record<string, UserProfile> = {};
    if (allMemberIds.length > 0) {
      try {
        const allMembers = await getMembers();
        const memberLookup = new Map(allMembers.map(m => [m.id, m]));
        allMemberIds.forEach(id => {
          const found = memberLookup.get(id);
          if (found) membersMap[id] = found;
        });
      } catch (err) {
        console.warn("Failed to fetch member details for threads:", err);
      }
    }

    const resolvedThreads = threads.map(t => {
      const threadMembers = t.memberIds.map(id => membersMap[id]).filter(Boolean);
      let displayTitle = t.title;
      if (threadMembers.length > 0 && (!t.title || t.title.startsWith("Conversation") || t.title.includes(","))) {
        displayTitle = threadMembers.filter(m => m.id !== auth.currentUser?.uid).map(m => m.displayName).join(", ") || "Direct Message";
      }
      return {
        ...t,
        members: threadMembers,
        displayTitle
      };
    });

    callback(resolvedThreads.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
  });
}

export function subscribeToThreadMessages(threadId: string, callback: (messages: Message[]) => void): () => void {
  const q = query(
    collection(db, "messageThreads", threadId, "messages"),
    orderBy("createdAt", "desc"),
    limit(50)
  );
  return onSnapshot(q, async (snapshot) => {
    const messages = snapshot.docs.map(doc => normalizeMessage(doc.id, doc.data()));
    const senderIds = [...new Set(messages.map(m => m.senderId).filter(Boolean))];

    const sendersMap: Record<string, UserProfile> = {};
    if (senderIds.length > 0) {
      try {
        const allMembers = await getMembers();
        const memberLookup = new Map(allMembers.map(m => [m.id, m]));
        senderIds.forEach(id => {
          const found = memberLookup.get(id);
          if (found) sendersMap[id] = found;
        });
      } catch (err) {
        console.warn("Failed to fetch sender details for messages:", err);
      }
    }

    const resolvedMessages = messages.map(m => {
      const sender = sendersMap[m.senderId];
      return {
        ...m,
        sender,
        senderName: sender?.displayName || m.senderName,
        senderAvatarUrl: sender?.photoUrl || m.senderAvatarUrl
      };
    });

    callback(resolvedMessages.sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return timeA - timeB;
    }));
  });
}

export async function getThreadMessages(threadId: string): Promise<Message[]> {
  const q = query(
    collection(db, "messageThreads", threadId, "messages"),
    orderBy("createdAt", "desc"),
    limit(50)
  );
  const snap = await getDocs(q);
  const messages = snap.docs.map(doc => normalizeMessage(doc.id, doc.data()));
  const senderIds = [...new Set(messages.map(m => m.senderId).filter(Boolean))];

  const sendersMap: Record<string, UserProfile> = {};
  if (senderIds.length > 0) {
    try {
      const allMembers = await getMembers();
      const memberLookup = new Map(allMembers.map(m => [m.id, m]));
      senderIds.forEach(id => {
        const found = memberLookup.get(id);
        if (found) sendersMap[id] = found;
      });
    } catch (err) {
      console.warn("Failed to fetch sender details for messages:", err);
    }
  }

  return messages.map(m => {
    const sender = sendersMap[m.senderId];
    return {
      ...m,
      sender,
      senderName: sender?.displayName || m.senderName,
      senderAvatarUrl: sender?.photoUrl || m.senderAvatarUrl
    };
  }).sort((a, b) => {
    const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return timeA - timeB;
  });
}

export async function getOrCreateMessageThread(memberIds: string[], title = "", eventId = ""): Promise<MessageThread | null> {
    const user = auth.currentUser;
    if (!user) return null;
    const ids = [...new Set([user.uid, ...memberIds])].sort();
    if (ids.length < 2) return null;
    const key = ids.join("|");
    const existing = await getDocs(query(collection(db, "messageThreads"), where("memberIds", "array-contains", user.uid), where("threadKey", "==", key)));
    if (!existing.empty) return normalizeThread(existing.docs[0].id, existing.docs[0].data());
    const members = await getMembers();
    const fallbackTitle = ids.filter(id => id !== user.uid).map(id => members.find(m => m.id === id)?.displayName ?? "Member").join(", ");
    const docRef = await addDoc(collection(db, "messageThreads"), { memberIds: ids, threadKey: key, title: title || fallbackTitle, eventId, createdAt: new Date(), updatedAt: new Date(), lastMessage: "" });
    const snap = await getDoc(docRef);
    return normalizeThread(docRef.id, snap.data()!);
}

export async function updateThreadReadReceipt(threadId: string, memberId: string): Promise<void> {
  try {
    const docRef = doc(db, "messageThreads", threadId);
    await updateDoc(docRef, {
      [`lastRead.${memberId}`]: new Date().toISOString()
    });
  } catch (e) {
    console.warn("[updateThreadReadReceipt] Error:", e);
  }
}
export async function sendMessage(threadId: string, text: string, imageUrl = ""): Promise<{ success: boolean; message: string }> {
  if (!auth.currentUser) return { success: false, message: "Not signed in." };
  try {
    await addDoc(collection(db, "messageThreads", threadId, "messages"), { senderId: auth.currentUser.uid, text, imageUrl, createdAt: new Date().toISOString() });
    await updateDoc(doc(db, "messageThreads", threadId), { lastMessage: text || "Sent an image", updatedAt: new Date() });
    
    // Trigger push notification to other thread members
    try {
      const token = await getCurrentIdToken();
      if (token) {
        apiFetch('/api/notifications/send', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ThreadId: threadId,
            MessageText: text
          })
        }).catch(err => console.warn('[sendMessage] Failed to send push notification API trigger:', err));
      }
    } catch (pushErr) {
      console.warn('[sendMessage] Failed to invoke notifications API:', pushErr);
    }

    return { success: true, message: "Message sent successfully." };
  } catch (e: any) {
    return { success: false, message: e.message };
  }
}
export async function getMessagingBootstrap(memberId = "", eventId = "", status = ""): Promise<{ members: UserProfile[]; initialMemberIds: string[]; initialTitle: string; eventId: string; currentMemberId: string; canBroadcast: boolean }> {
    const members = await getMembers();
    const initialIds: string[] = [];
    let initialTitle = "";
    if (memberId) { initialIds.push(memberId); initialTitle = members.find(m => m.id === memberId)?.displayName ?? ""; }
    if (eventId) {
        const eSnap = await getDoc(doc(db, "events", eventId));
        if (eSnap.exists()) {
            const rsvps = eSnap.data().rsvps ?? {};
            Object.entries(rsvps).forEach(([id, r]: [string, any]) => { if (!status || r?.status === status) initialIds.push(id); });
            initialTitle = eSnap.data().title ? `${eSnap.data().title} attendees` : "Event attendees";
        }
    }
    const currentP = auth.currentUser ? await getMemberProfile(auth.currentUser.uid) : null;
    return { members, initialMemberIds: [...new Set(initialIds)].filter(id => id !== auth.currentUser?.uid), initialTitle, eventId, currentMemberId: auth.currentUser?.uid ?? "", canBroadcast: currentP?.isAdmin === true };
}

// --- Admin Operations ---

let cachedAdminEvents: { data: any[]; timestamp: number } | null = null;
let inflightAdminEventsPromise: Promise<any[]> | null = null;
const ADMIN_EVENTS_CACHE_TTL_MS = 60_000;

export function invalidateAdminEventsCache() {
    cachedAdminEvents = null;
    inflightAdminEventsPromise = null;
}

export async function getAdminEvents(forceRefresh = false): Promise<any[]> {
    const now = Date.now();
    if (forceRefresh) {
        cachedAdminEvents = null;
        inflightAdminEventsPromise = null;
    } else if (cachedAdminEvents && (now - cachedAdminEvents.timestamp < ADMIN_EVENTS_CACHE_TTL_MS)) {
        return cachedAdminEvents.data;
    }

    if (inflightAdminEventsPromise) {
        return inflightAdminEventsPromise;
    }

    inflightAdminEventsPromise = (async () => {
        try {
            // 1. Try fast in-memory cached backend endpoint
            try {
                const token = await getCurrentIdToken();
                const response = await apiFetch('/api/EventsAdmin', {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        const mems = await getMembers().catch(() => []);
                        const memMap = new Map(mems.map(m => [m.id, m]));
                        const result = data.map((ev: any) => {
                            const rd = ev.date ?? ev.eventDate ?? ev.startDate ?? ev.startsAt ?? null;
                            const ed = normalizeEventDate(rd);
                            const hosts = (ev.hostMemberIds ?? []).map((id: string) => memMap.get(id)).filter(Boolean);
                            return {
                                ...ev,
                                date: ed?.toISOString().split('T')[0] ?? (ev.date || ""),
                                timeDisplay: ev.timeDisplay || formatEventTime(ev.startTime ?? "", ev.endTime ?? "", ev.hasEndTime === true),
                                hosts: hosts.length > 0 ? hosts : (ev.hosts || [])
                            };
                        });
                        cachedAdminEvents = { data: result, timestamp: Date.now() };
                        return result;
                    }
                }
            } catch (apiErr) {
                console.warn("[getAdminEvents] Backend /api/EventsAdmin unreachable, falling back to Firestore:", apiErr);
            }

            return cachedAdminEvents?.data || [];
        } finally {
            inflightAdminEventsPromise = null;
        }
    })();

    return inflightAdminEventsPromise;
}

export async function saveEvent(event: any): Promise<{ success: boolean; message: string }> {
    const { id, pointsPackage, venue, hosts, photoUrlsText, ...rest } = event;
    const data: any = { 
        ...rest,
        pointsPackageId: pointsPackage?.id || event.pointsPackageId || "",
        venueId: event.venueId || venue?.id || "",
        venueName: event.venueName || venue?.name || "",
        venueAddress: event.venueAddress || venue?.address || "",
        venuePhotoUrl: event.venuePhotoUrl || venue?.photoUrl || "",
        hostMemberIds: hosts ? hosts.map((h: any) => h.id) : (event.hostMemberIds || []),
        updatedAt: new Date(), 
        updatedBy: auth.currentUser?.uid 
    };

    // Update the Venue Template if one is selected and details changed
    if (data.venueId) {
        try {
            const venueRef = doc(db, "venues", data.venueId);
            const venueSnap = await getDoc(venueRef);
            if (venueSnap.exists()) {
                const venueData = venueSnap.data();
                if (venueData.name !== data.venueName || venueData.address !== data.venueAddress) {
                    await updateDoc(venueRef, {
                        name: data.venueName,
                        address: data.venueAddress,
                        updatedAt: new Date()
                    });
                }
            }
        } catch (e) {
            console.warn("[saveEvent] Failed to update venue template:", e);
        }
    }

    // Default booleans safely if undefined or not boolean
    if (typeof data.isActive !== 'boolean') data.isActive = true;
    if (typeof data.isPublished !== 'boolean') data.isPublished = true;
    if (typeof data.isFeatured !== 'boolean') data.isFeatured = false;
    if (typeof data.hasEndTime !== 'boolean') data.hasEndTime = false;
    if (typeof data.is_deleted !== 'boolean') data.is_deleted = false;
    
    // Safety: ensure no undefined values
    Object.keys(data).forEach(key => {
        if (data[key] === undefined) data[key] = null;
    });

    let savedEventId = id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15));

    // Primary write to PostgreSQL (authoritative single source of truth)
    try {
        const token = await getCurrentIdToken();
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timeoutId = controller ? setTimeout(() => controller.abort(), 30000) : null;
        const response = await apiFetch('/api/events', {
            method: 'POST',
            signal: controller?.signal,
            headers: { 
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': 'Bearer ' + token } : {})
            },
            body: JSON.stringify({
                id: savedEventId,
                ...data
            })
        });
        if (timeoutId) clearTimeout(timeoutId);
        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            const errMsg = errData.message || `Server returned ${response.status}: Failed to save event.`;
            console.error("[saveEvent] Server error saving event:", response.status, errMsg);
            return { success: false, message: errMsg };
        }
    } catch (pgErr: any) {
        console.warn("[saveEvent] Failed to sync to PostgreSQL:", pgErr);
        const isAbort = pgErr?.name === 'AbortError' || pgErr?.message?.includes('aborted');
        const errMsg = isAbort 
            ? "Request timed out while saving the event. If uploading large photos, please ensure a stable connection."
            : (pgErr?.message || "Failed to save event to database.");
        return { success: false, message: errMsg };
    }

    invalidateUpcomingEventsCache();
    invalidateAdminEventsCache();
    return { success: true, message: "Event saved successfully." };
}

export async function updateEventStatus(eventId: string, isPublished: boolean): Promise<{ success: boolean; message: string }> {
    try {
        const token = await getCurrentIdToken();
        await apiFetch('/api/events', {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ id: eventId, isPublished })
        });
    } catch (pgErr) {
        console.warn("[updateEventStatus] Failed to sync to PostgreSQL:", pgErr);
    }

    // PostgreSQL is the single source of truth; no Firestore shadow write

    invalidateUpcomingEventsCache();
    invalidateAdminEventsCache();
    return { success: true, message: "Event status updated." };
}

export async function deleteEvent(eventId: string): Promise<{ success: boolean; message: string }> {
    try {
        const token = await getCurrentIdToken();
        await apiFetch(`/api/events/${encodeURIComponent(eventId)}`, {
            method: 'DELETE',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
    } catch (pgErr) {
        console.warn("[deleteEvent] Failed to sync to PostgreSQL:", pgErr);
    }

    // PostgreSQL is the single source of truth; no Firestore shadow write

    invalidateUpcomingEventsCache();
    invalidateAdminEventsCache();
    return { success: true, message: "Event deleted successfully." };
}

let cachedVenues: { data: Venue[]; timestamp: number } | null = null;
let inflightVenuesPromise: Promise<Venue[]> | null = null;
const VENUES_CACHE_TTL_MS = 900_000; // 15 minutes

export function invalidateVenuesCache() {
  cachedVenues = null;
  inflightVenuesPromise = null;
  removeSessionCached('acs_cache_venues');
}

export async function getVenues(forceRefresh = false): Promise<Venue[]> {
    const now = Date.now();
    if (forceRefresh) {
        cachedVenues = null;
        inflightVenuesPromise = null;
        removeSessionCached('acs_cache_venues');
    } else {
        if (cachedVenues && (now - cachedVenues.timestamp < VENUES_CACHE_TTL_MS)) {
            return cachedVenues.data;
        }
        const sessionData = getSessionCached<Venue[]>('acs_cache_venues', VENUES_CACHE_TTL_MS);
        if (sessionData) {
            cachedVenues = { data: sessionData, timestamp: now };
            return sessionData;
        }
    }

    if (inflightVenuesPromise) {
        return inflightVenuesPromise;
    }

    inflightVenuesPromise = (async () => {
        try {
            try {
                const response = await apiFetch('/api/events/venues');
                if (response.ok) {
                    const pgVenues = await response.json();
                    const result = pgVenues.map((v: any) => ({
                        ...v,
                        photoUrl: fixPhotoUrl(v.photoUrl)
                    })).sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));
                    cachedVenues = { data: result, timestamp: Date.now() };
                    setSessionCached('acs_cache_venues', result);
                    return result;
                }
            } catch (err) {
                console.warn("[getVenues] PostgreSQL endpoint unreachable, falling back to Firestore:", err);
            }

            const snap = await getDocs(collection(db, "venues"));
            const result = snap.docs
                .filter(d => d.data().is_deleted !== true)
                .map(d => ({ id: d.id, ...d.data(), photoUrl: fixPhotoUrl(d.data().photoUrl) } as Venue))
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            cachedVenues = { data: result, timestamp: Date.now() };
            setSessionCached('acs_cache_venues', result);
            return result;
        } catch (e) {
            console.error("[getVenues] Error fetching venues:", e);
            return cachedVenues?.data || [];
        } finally {
            inflightVenuesPromise = null;
        }
    })();

    return inflightVenuesPromise;
}

export async function saveVenue(venue: any): Promise<{ success: boolean; message: string }> {
    const data = { ...venue, updatedAt: new Date() };

    // 1. If it's a base64 local image upload, upload it to storage
    if (data.photoUrl && data.photoUrl.startsWith('data:')) {
        try {
            const tempId = venue.id || `venue_${Date.now()}`;
            const compressed = await compressImage(data.photoUrl);
            const storagePath = `venue-photos/${tempId}`;
            const uploadedUrl = await uploadDataUrl(storagePath, compressed);
            data.photoUrl = uploadedUrl;
        } catch (e) {
            console.error("[saveVenue] Failed to upload local venue photo to storage:", e);
        }
    }
    // 2. If it's an external link (like Places API), download and upload it via Cloud Function
    else if (data.photoUrl && data.photoUrl.startsWith('http') && !data.photoUrl.includes('firebasestorage.googleapis.com')) {
        try {
            const uploadFn = httpsCallable(functions, 'uploadVenuePhotoFromUrl');
            const tempId = venue.id || `venue_${Date.now()}`;
            const res = await uploadFn({ url: data.photoUrl, venueId: tempId });
            const resData = res.data as any;
            if (resData && resData.success && resData.photoUrl) {
                data.photoUrl = resData.photoUrl;
            }
        } catch (e) {
            console.error("[saveVenue] Failed to upload venue photo from external URL:", e);
        }
    }

    let savedVenueId = venue.id || doc(collection(db, "venues")).id;

    // Primary write to PostgreSQL
    try {
        const token = await getCurrentIdToken();
        await apiFetch('/api/events/venues', {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
                id: savedVenueId,
                ...data
            })
        });
    } catch (pgErr) {
        console.warn("[saveVenue] Failed to sync to PostgreSQL:", pgErr);
    }

    // Shadow write to Firestore
    try {
        await setDoc(doc(db, "venues", savedVenueId), data, { merge: true });
    } catch (fsErr) {
        console.warn("[saveVenue] Failed shadow write to Firestore:", fsErr);
    }

    invalidateVenuesCache();
    return { success: true, message: "Venue saved successfully." };
}

export async function deleteVenue(venueId: string): Promise<{ success: boolean; message: string }> {
    try {
        const token = await getCurrentIdToken();
        await apiFetch(`/api/events/venues/${encodeURIComponent(venueId)}`, {
            method: 'DELETE',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
    } catch (pgErr) {
        console.warn("[deleteVenue] Failed to sync to PostgreSQL:", pgErr);
    }

    try {
        await updateDoc(doc(db, "venues", venueId), { is_deleted: true, updatedAt: new Date() });
    } catch (fsErr) {
        console.warn("[deleteVenue] Failed shadow delete in Firestore:", fsErr);
    }

    invalidateVenuesCache();
    return { success: true, message: "Venue deleted successfully." };
}

export async function updateMemberAdmin(memberId: string, isAdmin: boolean): Promise<{ success: boolean; message: string }> {
    await updateDoc(doc(db, "members", memberId), { isAdmin, updatedAt: new Date() });
    try {
        await apiFetch(`/api/members/${encodeURIComponent(memberId)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isAdmin })
        });
    } catch (pgErr) {
        console.warn("[updateMemberAdmin] Failed to sync to PostgreSQL:", pgErr);
    }
    return { success: true, message: "Member admin permissions updated." };
}

export async function updateMemberStatus(memberId: string, isLocked: boolean, isBanned: boolean): Promise<{ success: boolean; message: string }> {
    await updateDoc(doc(db, "members", memberId), { isLocked, isBanned, updatedAt: new Date() });
    try {
        await apiFetch(`/api/members/${encodeURIComponent(memberId)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isLocked, isBanned })
        });
    } catch (pgErr) {
        console.warn("[updateMemberStatus] Failed to sync to PostgreSQL:", pgErr);
    }
    return { success: true, message: "Member status updated." };
}

export async function saveBostonEvent(event: any): Promise<{ success: boolean; message: string }> {
    if (!auth.currentUser) return { success: false, message: "Not signed in." };
    const data = { ...event, type: "boston", updatedAt: new Date(), updatedBy: auth.currentUser.uid };
    
    // Remove complex mapping lists before saving
    delete data.winningMembers;
    delete data.losingMembers;
    delete data.participants;

    if (event.id) await setDoc(doc(db, "gameEvents", event.id), data, { merge: true });
    else await addDoc(collection(db, "gameEvents"), { ...data, createdAt: new Date() });
    return { success: true, message: "Boston event saved successfully." };
}

export async function deleteBostonEvent(id: string): Promise<{ success: boolean; message: string }> {
    if (!auth.currentUser) return { success: false, message: "Not signed in." };
    try {
        await deleteDoc(doc(db, "gameEvents", id));
        cachedBostonEvents = null;
        removeSessionCached('acs_cache_bostons');
        return { success: true, message: "Boston event deleted successfully." };
    } catch (err: any) {
        console.error("Failed to delete Boston event:", err);
        return { success: false, message: err.message || "Failed to delete Boston event." };
    }
}

export async function seedInitialCollections(): Promise<{ success: boolean; message: string }> {
    await addDoc(collection(db, "points_packages"), { name: "Standard", rsvpPoints: 5, checkInPoints: 15, isDefault: true });
    return { success: true, message: "Database seeding complete." };
}

export async function seedDummyDashboardData(): Promise<{ success: boolean; message: string }> {
    if (!auth.currentUser) return { success: false, message: "Not signed in." };
    await setDoc(doc(db, "performance", auth.currentUser.uid), { totalWins: 10, currentRank: 5, updatedAt: new Date() });
    return { success: true, message: "Dashboard dummy data seeded." };
}

export async function getEventRatingForUser(eventId: string): Promise<any | null> {
    if (!auth.currentUser || !eventId) return null;
    try {
        const ratingDoc = await getDoc(doc(db, "eventRatings", `${eventId}_${auth.currentUser.uid}`));
        return ratingDoc.exists() ? ratingDoc.data() : null;
    } catch (e) {
        console.error("[getEventRatingForUser] Error:", e);
        return null;
    }
}

export async function getEventRatings(eventId: string): Promise<any[]> {
    if (!eventId) return [];
    try {
        const q = query(collection(db, "eventRatings"), where("eventId", "==", eventId));
        const snap = await getDocs(q);
        return snap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => {
                const dateA = (a as any).createdAt || "";
                const dateB = (b as any).createdAt || "";
                return dateB.localeCompare(dateA);
            });
    } catch (e) {
        console.error("[getEventRatings] Error:", e);
        return [];
    }
}

export async function submitEventRating(eventId: string, rating: number, comment: string, answers: any): Promise<{ success: boolean; message?: string }> {
    if (!auth.currentUser) return { success: false, message: "Not signed in." };
    const uid = auth.currentUser.uid;
    const p = await getMemberProfile(uid);
    const displayName = p ? (p.displayName || p.nickname || "Member") : "Member";
    const avatarUrl = fixPhotoUrl(p?.avatarUrl || p?.photoUrl || "");

    const ratingRef = doc(db, "eventRatings", `${eventId}_${uid}`);
    const eventRef = doc(db, "events", eventId);

    const newRatingDoc = {
        eventId,
        memberId: uid,
        memberDisplayName: displayName,
        memberAvatarUrl: avatarUrl,
        rating,
        comment: comment || "",
        answers: answers || {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    };

    try {
        await setDoc(ratingRef, newRatingDoc, { merge: true });

        // Update event rating aggregation stats safely
        try {
            const ratingsSnap = await getDocs(query(collection(db, "eventRatings"), where("eventId", "==", eventId)));
            let totalSum = 0;
            let count = 0;
            ratingsSnap.forEach(d => {
                const r = d.data();
                if (typeof r.rating === 'number') {
                    totalSum += r.rating;
                    count += 1;
                }
            });
            const avg = count > 0 ? (totalSum / count) : 0;
            // Rating reviews stored in eventRatings collection; event stats aggregated in backend
        } catch (aggErr) {
            console.warn("[submitEventRating] Aggregation update on event doc failed (review doc was saved):", aggErr);
        }

        return { success: true };
    } catch (e: any) {
        console.error("[submitEventRating] Failed to submit rating:", e);
        return { success: false, message: e.message };
    }
}

export async function deleteMember(uid: string): Promise<any> {
    const deleteFn = httpsCallable(functions, 'deleteMember');
    try {
        const result = await deleteFn({ uid });
        return result.data;
    } catch (e: any) {
        console.error("[deleteMember] Error:", e);
        return { success: false, message: e.message };
    }
}

export async function submitFeedback({ type, severity, subject, description, contactEmail, attachmentDataUrl, appVersion, userAgent, submittedFromRoute }: any): Promise<{ success: boolean; message?: string }> {
    if (!auth.currentUser) return { success: false, message: "You must be signed in to submit feedback." };
    const uid = auth.currentUser.uid;
    
    try {
        let attachmentUrl = "";
        if (attachmentDataUrl) {
            const compressed = await compressImage(attachmentDataUrl);
            const path = `feedback-attachments/${uid}/${Date.now()}`;
            attachmentUrl = await uploadDataUrl(path, compressed);
        }

        // Get user profile for displayName
        const memberProfile = await getMemberProfile(uid);
        const displayName = memberProfile ? memberProfile.displayName : "Member";

        await addDoc(collection(db, "feedback"), {
            type,
            severity: type === "bug" ? severity : null,
            subject,
            description,
            contactEmail,
            userId: uid,
            displayName,
            attachmentUrl: attachmentUrl || null,
            appVersion,
            userAgent,
            submittedFromRoute,
            createdAt: new Date()
        });

        return { success: true };
    } catch (e: any) {
        console.error("[submitFeedback] Error:", e);
        return { success: false, message: e.message };
    }
}

export async function getPhotoLikes(photoUrl: string): Promise<{ count: number; likedByMe: boolean }> {
  try {
    const q = query(collection(db, 'gallery_photo_likes'), where('photoUrl', '==', photoUrl));
    const snap = await getDocs(q);
    const likedByMe = auth.currentUser ? snap.docs.some(d => d.data().userId === auth.currentUser?.uid) : false;
    return { count: snap.size, likedByMe };
  } catch (e) {
    console.error("Error in getPhotoLikes:", e);
    return { count: 0, likedByMe: false };
  }
}

export async function togglePhotoLike(photoUrl: string): Promise<{ success: boolean; liked: boolean; count: number }> {
  const user = auth.currentUser;
  if (!user) return { success: false, liked: false, count: 0 };
  
  try {
    const q = query(
      collection(db, 'gallery_photo_likes'), 
      where('photoUrl', '==', photoUrl), 
      where('userId', '==', user.uid)
    );
    const snap = await getDocs(q);
    let liked = false;
    if (!snap.empty) {
      await deleteDoc(doc(db, 'gallery_photo_likes', snap.docs[0].id));
      liked = false;
    } else {
      await addDoc(collection(db, 'gallery_photo_likes'), {
        photoUrl,
        userId: user.uid,
        likedAt: new Date().toISOString()
      });
      liked = true;
    }
    const likes = await getPhotoLikes(photoUrl);
    return { success: true, liked, count: likes.count };
  } catch (e) {
    console.error("Error toggling photo like:", e);
    return { success: false, liked: false, count: 0 };
  }
}

export async function getPhotoComments(photoUrl: string): Promise<any[]> {
  try {
    const q = query(
      collection(db, 'gallery_photo_comments'), 
      where('photoUrl', '==', photoUrl), 
      orderBy('createdAt', 'asc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt ? (typeof data.createdAt.toDate === 'function' ? data.createdAt.toDate().toISOString() : data.createdAt) : null
      };
    });
  } catch (e) {
    console.error("Error in getPhotoComments:", e);
    return [];
  }
}

export async function addPhotoComment(photoUrl: string, text: string): Promise<{ success: boolean; comment?: any }> {
  const user = auth.currentUser;
  if (!user) return { success: false };
  
  try {
    const profile = await getMemberProfile(user.uid);
    const comment = {
      photoUrl,
      userId: user.uid,
      userName: profile?.displayName || "Member",
      userPhotoUrl: profile?.photoUrl || "",
      text,
      createdAt: new Date()
    };
    const docRef = await addDoc(collection(db, 'gallery_photo_comments'), comment);
    return {
      success: true,
      comment: {
        id: docRef.id,
        ...comment,
        createdAt: comment.createdAt.toISOString()
      }
    };
  } catch (e) {
    console.error("Error adding photo comment:", e);
    return { success: false };
  }
}

// --- Bid Whist Games & Leaderboard Service ---

/**
 * Calculates Bid Whist points for a single hand based on:
 * - Bid (4, 5, 6, 7)
 * - Trump vs No Trump (No Trump doubles all points)
 * - Outcome (Made vs Set)
 * - Boston (13-trick shutout -> 7 pts for Trump, 14 pts for No Trump)
 * - If Set: opposing defending team gets the points of the bid
 */
export function calculateBidWhistHandPoints(
  handOrTeam: {
    biddingTeam: 1 | 2 | number;
    bid: number;
    trumpType: 'trump' | 'no_trump' | string;
    outcome: 'made' | 'set' | string;
    isBoston?: boolean;
  } | number,
  bidArg?: number,
  trumpTypeArg?: string,
  outcomeArg?: string,
  isBostonArg?: boolean
): { pointsTeam1: number; pointsTeam2: number } {
  const hand = (typeof handOrTeam === 'object' && handOrTeam !== null)
    ? (handOrTeam as any)
    : {
        biddingTeam: handOrTeam,
        bid: bidArg || 4,
        trumpType: trumpTypeArg || 'trump',
        outcome: outcomeArg || 'made',
        isBoston: Boolean(isBostonArg)
      };
  const isNT = hand.trumpType === 'no_trump';
  const bidVal = Number(hand.bid) || 4;
  const isMade = hand.outcome === 'made';
  const isBoston = isMade && hand.isBoston === true;

  let points = 0;
  if (isBoston) {
    points = isNT ? 14 : 7;
  } else {
    points = isNT ? bidVal * 2 : bidVal;
  }

  if (isMade) {
    // Bidding team receives points
    return {
      pointsTeam1: hand.biddingTeam === 1 ? points : 0,
      pointsTeam2: hand.biddingTeam === 2 ? points : 0,
    };
  } else {
    // Defending team receives points
    return {
      pointsTeam1: hand.biddingTeam === 1 ? 0 : points,
      pointsTeam2: hand.biddingTeam === 2 ? 0 : points,
    };
  }
}

function normalizeGameRecord(id: string, data: DocumentData): GameRecord {
  const createdAt = parseDate(data.createdAt);
  const updatedAt = parseDate(data.updatedAt);
  const rawHands: any[] = Array.isArray(data.hands) ? data.hands : [];

  const hands: GameHand[] = rawHands.map((h, idx) => {
    const rawBiddingTeam = h.biddingTeam !== undefined ? h.biddingTeam : (h.team !== undefined ? h.team : 1);
    const biddingTeam = (rawBiddingTeam === 2 || String(rawBiddingTeam).trim() === '2') ? 2 : 1;
    const bid = Number(h.bid) || 4;
    
    // Robust Trump type parsing
    const rawTrump = String(h.trumpType || '').toLowerCase().replace(/[\s_-]/g, '');
    const isNT = rawTrump.includes('notrump') || rawTrump === 'nt' || h.isNoTrump === true;
    const trumpType = (isNT ? 'no_trump' : 'trump') as 'trump' | 'no_trump';

    // Robust Direction parsing
    const rawDir = String(h.direction || '').toLowerCase().trim();
    const isDown = rawDir.startsWith('down') || rawDir === 'd' || h.isDowntown === true;
    const direction = (isDown ? 'down' : 'up') as 'up' | 'down';

    // Robust Outcome parsing
    const rawOutcome = String(h.outcome || '').toLowerCase().trim();
    const isSet = rawOutcome === 'set' || rawOutcome === 'failed' || rawOutcome === 'lost' || h.isSet === true;
    const outcome = (isSet ? 'set' : 'made') as 'made' | 'set';

    const rawPoints1 = typeof h.pointsTeam1 === 'number' ? h.pointsTeam1 : 0;
    const rawPoints2 = typeof h.pointsTeam2 === 'number' ? h.pointsTeam2 : 0;
    const isBoston = outcome === 'made' && (h.isBoston === true || Number(bid) === 7 || (isNT && (rawPoints1 === 14 || rawPoints2 === 14)));

    // If points are stored, use them; otherwise compute
    let pointsTeam1 = typeof h.pointsTeam1 === 'number' ? h.pointsTeam1 : 0;
    let pointsTeam2 = typeof h.pointsTeam2 === 'number' ? h.pointsTeam2 : 0;

    if (h.pointsTeam1 === undefined || h.pointsTeam2 === undefined) {
      const computed = calculateBidWhistHandPoints({ biddingTeam, bid, trumpType, outcome, isBoston });
      pointsTeam1 = computed.pointsTeam1;
      pointsTeam2 = computed.pointsTeam2;
    }

    return {
      handNumber: typeof h.handNumber === 'number' ? h.handNumber : idx + 1,
      biddingTeam,
      bid,
      trumpType,
      direction,
      outcome,
      isBoston,
      pointsTeam1,
      pointsTeam2,
      notes: h.notes || ''
    };
  });

  const team1Score = typeof data.team1FinalScore === 'number' 
    ? data.team1FinalScore 
    : hands.reduce((sum, h) => sum + h.pointsTeam1, 0);
  const team2Score = typeof data.team2FinalScore === 'number' 
    ? data.team2FinalScore 
    : hands.reduce((sum, h) => sum + h.pointsTeam2, 0);

  const winningTeam = (data.winningTeam === 2 || (data.winningTeam === undefined && team2Score > team1Score)) ? 2 : 1;
  const totalBostons = typeof data.totalBostons === 'number'
    ? data.totalBostons
    : hands.filter(h => h.isBoston).length;

  return {
    id,
    playedDate: data.playedDate || (createdAt ? createdAt.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)),
    playedTime: data.playedTime || '00:00',
    locationName: data.locationName || 'Clubhouse',
    eventId: data.eventId || '',
    scorecardId: data.scorecardId || data.scorecard_id || '',
    scorecardImageUrl: data.scorecardImageUrl || data.scorecard_image_url || (typeof data.scorecardImageDataUrl === 'string' && data.scorecardImageDataUrl.startsWith('data:') ? data.scorecardImageDataUrl : '') || '',
    scorecardImageDataUrl: data.scorecardImageDataUrl || (typeof data.scorecardImageUrl === 'string' && data.scorecardImageUrl.startsWith('data:') ? data.scorecardImageUrl : '') || '',
    team1Name: data.team1Name || `${data.team1Player1Name || 'P1'} & ${data.team1Player2Name || 'P2'}`,
    team1Player1Id: data.team1Player1Id || '',
    team1Player1Name: data.team1Player1Name || 'Player 1',
    team1Player2Id: data.team1Player2Id || '',
    team1Player2Name: data.team1Player2Name || 'Player 2',
    team2Name: data.team2Name || `${data.team2Player1Name || 'P3'} & ${data.team2Player2Name || 'P4'}`,
    team2Player1Id: data.team2Player1Id || '',
    team2Player1Name: data.team2Player1Name || 'Player 3',
    team2Player2Id: data.team2Player2Id || '',
    team2Player2Name: data.team2Player2Name || 'Player 4',
    participantMemberIds: Array.isArray(data.participantMemberIds) ? data.participantMemberIds : [],
    team1FinalScore: team1Score,
    team2FinalScore: team2Score,
    winningTeam,
    totalBostons,
    notes: data.notes || '',
    hands,
    createdAt: createdAt ? createdAt.toISOString() : new Date().toISOString(),
    updatedAt: updatedAt ? updatedAt.toISOString() : new Date().toISOString(),
    createdBy: data.createdBy || ''
  };
}

let cachedGameRecords: { data: GameRecord[]; timestamp: number } | null = null;
let inflightGamesPromise: Promise<GameRecord[]> | null = null;
const GAMES_CACHE_TTL_MS = 30_000; // 30 seconds for quick reactive updates

export function invalidateGameRecordsCache() {
  cachedGameRecords = null;
  inflightGamesPromise = null;
  removeSessionCached('acs_cache_games');
  invalidateLeaderboardCache();
}

export async function getGameRecords(forceRefresh = false): Promise<GameRecord[]> {
  const now = Date.now();
  if (forceRefresh) {
    cachedGameRecords = null;
    inflightGamesPromise = null;
    removeSessionCached('acs_cache_games');
    invalidateLeaderboardCache();
  } else {
    if (cachedGameRecords && (now - cachedGameRecords.timestamp < GAMES_CACHE_TTL_MS)) {
      return cachedGameRecords.data;
    }
    const sessionData = getSessionCached<GameRecord[]>('acs_cache_games', GAMES_CACHE_TTL_MS);
    if (sessionData) {
      cachedGameRecords = { data: sessionData, timestamp: now };
      return sessionData;
    }
  }

  if (inflightGamesPromise) {
    return inflightGamesPromise;
  }

  inflightGamesPromise = (async () => {
    try {
      const url = forceRefresh ? '/api/games?forceRefresh=true' : '/api/games';
      const response = await apiFetch(url);
      if (response.ok) {
        const pgGames = await response.json();
        const result = pgGames.map((g: any) => normalizeGameRecord(g.id, g)).sort((a: any, b: any) => {
          const dateA = `${a.playedDate} ${a.playedTime || '00:00'}`;
          const dateB = `${b.playedDate} ${b.playedTime || '00:00'}`;
          return dateB.localeCompare(dateA);
        });
        cachedGameRecords = { data: result, timestamp: Date.now() };
        setSessionCached('acs_cache_games', result);
        return result;
      } else {
        console.warn('[getGameRecords] PostgreSQL endpoint returned status:', response.status);
      }
    } catch (e) {
      console.warn('[getGameRecords] Error loading game records from PostgreSQL, falling back to Firestore:', e);
    }

    // Fallback to Firestore if PostgreSQL API is unavailable or returns error
    try {
      const snap = await getDocs(query(collection(db, 'games'), orderBy('playedDate', 'desc')));
      const firestoreGames = snap.docs
        .map(d => normalizeGameRecord(d.id, d.data()))
        .filter(g => !(g as any).isDeleted)
        .sort((a, b) => {
          const dateA = `${a.playedDate} ${a.playedTime || '00:00'}`;
          const dateB = `${b.playedDate} ${b.playedTime || '00:00'}`;
          return dateB.localeCompare(dateA);
        });
      cachedGameRecords = { data: firestoreGames, timestamp: Date.now() };
      setSessionCached('acs_cache_games', firestoreGames);
      return firestoreGames;
    } catch (fsErr) {
      console.error('[getGameRecords] Firestore fallback failed:', fsErr);
      return cachedGameRecords?.data || [];
    } finally {
      inflightGamesPromise = null;
    }
  })();

  return inflightGamesPromise;
}

export async function getGameRecordById(id: string): Promise<GameRecord | null> {
  if (!id) return null;

  // 1. Check in-memory games list first (instant 0ms resolution)
  if (cachedGameRecords?.data) {
    const found = cachedGameRecords.data.find(g => g.id === id);
    if (found) return found;
  }

  // 2. Fetch single game from PostgreSQL endpoint
  try {
    const res = await apiFetch(`/api/games/${encodeURIComponent(id)}`);
    if (res.ok) {
      const data = await res.json();
      return normalizeGameRecord(data.id, data);
    }
  } catch (err) {
    console.warn(`[getGameRecordById] /api/games/${id} fetch failed, trying Firestore:`, err);
  }

  // 3. Fallback to Firestore single doc
  try {
    const snap = await getDoc(doc(db, 'games', id));
    if (snap.exists()) {
      return normalizeGameRecord(snap.id, snap.data());
    }
  } catch (err) {
    console.error(`[getGameRecordById] Firestore fetch failed for game ${id}:`, err);
  }

  return null;
}

function normalizeArenaGameRecord(id: string, data: DocumentData): ArenaGameRecord {
  const createdAt = parseDate(data.createdAt);
  const rawHands: any[] = Array.isArray(data.hands) ? data.hands : [];

  const hands: GameHand[] = rawHands.map((h, idx) => {
    const biddingTeam = (h.biddingTeam === 2 || String(h.biddingTeam).trim() === '2') ? 2 : 1;
    const bid = Number(h.bid) || 4;
    const rawTrump = String(h.trumpType || '').toLowerCase().replace(/[\s_-]/g, '');
    const isNT = rawTrump.includes('notrump') || rawTrump === 'nt' || h.isNoTrump === true || h.trumpSuit === 'NT';
    const trumpType = (isNT ? 'no_trump' : 'trump') as 'trump' | 'no_trump';

    const rawDir = String(h.direction || '').toLowerCase().trim();
    const isDown = rawDir.startsWith('down') || rawDir === 'd' || h.isDowntown === true;
    const direction = (isDown ? 'down' : 'up') as 'up' | 'down';

    const rawOutcome = String(h.outcome || '').toLowerCase().trim();
    const isSet = rawOutcome === 'set' || rawOutcome === 'failed' || rawOutcome === 'lost' || h.isSet === true;
    const outcome = (isSet ? 'set' : 'made') as 'made' | 'set';
    const rawPoints1 = typeof h.pointsTeam1 === 'number' ? h.pointsTeam1 : 0;
    const rawPoints2 = typeof h.pointsTeam2 === 'number' ? h.pointsTeam2 : 0;
    const isBoston = h.isBoston === true || Number(bid) === 7 || (isNT && (rawPoints1 === 14 || rawPoints2 === 14));

    let pointsTeam1 = typeof h.pointsTeam1 === 'number' ? h.pointsTeam1 : 0;
    let pointsTeam2 = typeof h.pointsTeam2 === 'number' ? h.pointsTeam2 : 0;
    if (h.pointsTeam1 === undefined || h.pointsTeam2 === undefined) {
      const computed = calculateBidWhistHandPoints({ biddingTeam, bid, trumpType, outcome, isBoston });
      pointsTeam1 = computed.pointsTeam1;
      pointsTeam2 = computed.pointsTeam2;
    }

    return {
      handNumber: typeof h.handNumber === 'number' ? h.handNumber : idx + 1,
      biddingTeam,
      bid,
      trumpType,
      direction,
      outcome,
      isBoston,
      pointsTeam1,
      pointsTeam2,
      notes: h.notes || h.summary || ''
    };
  });

  const rawT1 = data.team1 || {};
  const rawT2 = data.team2 || {};

  const team1Score = typeof rawT1.finalScore === 'number' ? rawT1.finalScore : typeof data.team1FinalScore === 'number' ? data.team1FinalScore : hands.reduce((sum, h) => sum + h.pointsTeam1, 0);
  const team2Score = typeof rawT2.finalScore === 'number' ? rawT2.finalScore : typeof data.team2FinalScore === 'number' ? data.team2FinalScore : hands.reduce((sum, h) => sum + h.pointsTeam2, 0);
  const winningTeam = (data.winningTeam === 2 || (data.winningTeam === undefined && team2Score > team1Score)) ? 2 : 1;
  const totalBostons = typeof data.totalBostons === 'number' ? data.totalBostons : hands.filter(h => h.isBoston).length;

  const team1Players = Array.isArray(rawT1.players) ? rawT1.players : [];
  const team2Players = Array.isArray(rawT2.players) ? rawT2.players : [];

  return {
    id,
    tableId: data.tableId || '',
    tournamentId: data.tournamentId || undefined,
    seriesId: data.seriesId || undefined,
    playedDate: data.playedDate || (createdAt ? createdAt.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)),
    playedTime: data.playedTime || '00:00',
    gameType: data.gameType || 'SEVEN_IN_OUT',
    seriesMode: data.seriesMode || undefined,
    matchMode: (data.matchMode || (data.tournamentId ? 'TOURNAMENT' : 'PvP')) as ArenaMatchMode,
    team1: {
      name: rawT1.name || 'Team 1',
      players: team1Players,
      finalScore: team1Score,
      gamesWon: typeof rawT1.gamesWon === 'number' ? rawT1.gamesWon : (winningTeam === 1 ? 1 : 0)
    },
    team2: {
      name: rawT2.name || 'Team 2',
      players: team2Players,
      finalScore: team2Score,
      gamesWon: typeof rawT2.gamesWon === 'number' ? rawT2.gamesWon : (winningTeam === 2 ? 1 : 0)
    },
    winningTeam,
    totalBostons,
    participantMemberIds: Array.isArray(data.participantMemberIds) ? data.participantMemberIds : [],
    hands,
    createdAt: createdAt ? createdAt.toISOString() : new Date().toISOString()
  };
}

let cachedArenaGameRecords: { data: ArenaGameRecord[]; timestamp: number } | null = null;
let inflightArenaGamesPromise: Promise<ArenaGameRecord[]> | null = null;

export function invalidateArenaGameRecordsCache() {
  cachedArenaGameRecords = null;
  inflightArenaGamesPromise = null;
  removeSessionCached('acs_cache_arena_games');
  invalidateArenaLeaderboardCache();
}

export async function getArenaGameRecords(forceRefresh = false): Promise<ArenaGameRecord[]> {
  const now = Date.now();
  if (forceRefresh) {
    cachedArenaGameRecords = null;
    inflightArenaGamesPromise = null;
    removeSessionCached('acs_cache_arena_games');
    invalidateArenaLeaderboardCache();
  } else {
    if (cachedArenaGameRecords && (now - cachedArenaGameRecords.timestamp < GAMES_CACHE_TTL_MS)) {
      return cachedArenaGameRecords.data;
    }
    const sessionData = getSessionCached<ArenaGameRecord[]>('acs_cache_arena_games', GAMES_CACHE_TTL_MS);
    if (sessionData) {
      cachedArenaGameRecords = { data: sessionData, timestamp: now };
      return sessionData;
    }
  }

  if (inflightArenaGamesPromise) {
    return inflightArenaGamesPromise;
  }

  inflightArenaGamesPromise = (async () => {
    try {
      const url = forceRefresh ? '/api/games/arena?forceRefresh=true' : '/api/games/arena';
      const response = await apiFetch(url);
      if (response.ok) {
        const pgArenaGames = await response.json();
        const result = pgArenaGames.map((g: any) => normalizeArenaGameRecord(g.id, g)).sort((a: any, b: any) => {
          const dateA = `${a.playedDate} ${a.playedTime || '00:00'}`;
          const dateB = `${b.playedDate} ${b.playedTime || '00:00'}`;
          return dateB.localeCompare(dateA);
        });
        cachedArenaGameRecords = { data: result, timestamp: Date.now() };
        setSessionCached('acs_cache_arena_games', result);
        return result;
      } else {
        console.warn('[getArenaGameRecords] PostgreSQL endpoint returned status:', response.status);
      }
    } catch (e) {
      console.warn('[getArenaGameRecords] Error loading arena game records from PostgreSQL, falling back to Firestore:', e);
    }

    // Fallback to Firestore if PostgreSQL API is unavailable or returns error
    try {
      const snap = await getDocs(query(collection(db, 'arena_games'), orderBy('createdAt', 'desc')));
      const firestoreArenaGames = snap.docs
        .map(d => normalizeArenaGameRecord(d.id, d.data()))
        .sort((a, b) => {
          const dateA = `${a.playedDate} ${a.playedTime || '00:00'}`;
          const dateB = `${b.playedDate} ${b.playedTime || '00:00'}`;
          return dateB.localeCompare(dateA);
        });
      cachedArenaGameRecords = { data: firestoreArenaGames, timestamp: Date.now() };
      setSessionCached('acs_cache_arena_games', firestoreArenaGames);
      return firestoreArenaGames;
    } catch (fsErr) {
      console.error('[getArenaGameRecords] Firestore fallback failed:', fsErr);
      return cachedArenaGameRecords?.data || [];
    } finally {
      inflightArenaGamesPromise = null;
    }
  })();

  return inflightArenaGamesPromise;
}


export async function saveGameRecord(game: Partial<GameRecord>): Promise<{ success: boolean; message: string; id?: string }> {
  if (!auth.currentUser) return { success: false, message: "Not signed in." };

  try {
    // 1. Process hands and compute exact points
    const hands: GameHand[] = (game.hands || []).map((h, idx) => {
      const computed = calculateBidWhistHandPoints({
        biddingTeam: h.biddingTeam,
        bid: h.bid,
        trumpType: h.trumpType,
        outcome: h.outcome,
        isBoston: h.isBoston
      });
      return {
        handNumber: idx + 1,
        biddingTeam: h.biddingTeam,
        bid: Number(h.bid) || 4,
        trumpType: h.trumpType || 'trump',
        direction: h.direction || 'up',
        outcome: h.outcome || 'made',
        isBoston: h.isBoston === true,
        pointsTeam1: computed.pointsTeam1,
        pointsTeam2: computed.pointsTeam2,
        notes: h.notes || ''
      };
    });

    const team1FinalScore = hands.reduce((sum, h) => sum + h.pointsTeam1, 0);
    const team2FinalScore = hands.reduce((sum, h) => sum + h.pointsTeam2, 0);
    const winningTeam = (team1FinalScore >= team2FinalScore) ? 1 : 2;
    const totalBostons = hands.filter(h => h.isBoston).length;

    const participantMemberIds = [
      game.team1Player1Id,
      game.team1Player2Id,
      game.team2Player1Id,
      game.team2Player2Id
    ].filter((id): id is string => !!id && typeof id === 'string');

    let docId = game.id || doc(collection(db, 'games')).id;

    let finalScorecardImageUrl = game.scorecardImageUrl || '';
    if (game.scorecardImageDataUrl) {
      try {
        finalScorecardImageUrl = await uploadScorecardImage(docId, game.scorecardImageDataUrl);
      } catch (uploadErr) {
        console.warn('[saveGameRecord] Failed to upload scorecard image to storage, preserving dataUrl fallback:', uploadErr);
        if (!finalScorecardImageUrl) {
          finalScorecardImageUrl = game.scorecardImageDataUrl;
        }
      }
    }
    if (!finalScorecardImageUrl && game.scorecardImageDataUrl) {
      finalScorecardImageUrl = game.scorecardImageDataUrl;
    }

    const cleanData: any = {
      playedDate: game.playedDate || new Date().toISOString().slice(0, 10),
      playedTime: game.playedTime || new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
      locationName: game.locationName || 'Clubhouse',
      eventId: game.eventId || '',
      scorecardId: game.scorecardId || '',
      scorecardImageUrl: finalScorecardImageUrl,
      scorecardImageDataUrl: game.scorecardImageDataUrl || (finalScorecardImageUrl.startsWith('data:') ? finalScorecardImageUrl : ''),
      team1Name: game.team1Name || `${game.team1Player1Name || 'P1'} & ${game.team1Player2Name || 'P2'}`,
      team1Player1Id: game.team1Player1Id || '',
      team1Player1Name: game.team1Player1Name || 'Player 1',
      team1Player2Id: game.team1Player2Id || '',
      team1Player2Name: game.team1Player2Name || 'Player 2',
      team2Name: game.team2Name || `${game.team2Player1Name || 'P3'} & ${game.team2Player2Name || 'P4'}`,
      team2Player1Id: game.team2Player1Id || '',
      team2Player1Name: game.team2Player1Name || 'Player 3',
      team2Player2Id: game.team2Player2Id || '',
      team2Player2Name: game.team2Player2Name || 'Player 4',
      participantMemberIds,
      team1FinalScore,
      team2FinalScore,
      winningTeam,
      totalBostons,
      notes: game.notes || '',
      hands,
      updatedAt: new Date(),
      createdBy: game.createdBy || auth.currentUser.uid
    };

    if (!game.id) {
      cleanData.createdAt = new Date();
    }

    await setDoc(doc(db, 'games', docId), cleanData, { merge: true });

    // Persist to PostgreSQL backend
    try {
      const token = await auth.currentUser?.getIdToken();
      await apiFetch('/api/games', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ ...cleanData, id: docId })
      });
    } catch (pgErr) {
      console.warn('[saveGameRecord] Failed to save to PostgreSQL backend:', pgErr);
    }

    // Invalidate games cache to ensure fresh state across the app
    invalidateGameRecordsCache();

    await writeAuditLog(
      game.id ? "Update Game Record" : "Record Match",
      docId,
      `Match between ${cleanData.team1Name} and ${cleanData.team2Name}. Final: ${team1FinalScore} - ${team2FinalScore}`
    );

    return { success: true, message: "Game record saved successfully.", id: docId };
  } catch (e: any) {
    console.error("[saveGameRecord] Error:", e);
    return { success: false, message: e.message || "Failed to save game record." };
  }
}

export async function deleteGameRecord(gameId: string): Promise<{ success: boolean; message: string }> {
  if (!auth.currentUser) return { success: false, message: "Not signed in." };

  try {
    try {
      const token = await auth.currentUser?.getIdToken();
      await apiFetch(`/api/games/${encodeURIComponent(gameId)}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
    } catch (pgErr) {
      console.warn('[deleteGameRecord] Failed to delete in PostgreSQL:', pgErr);
    }

    await deleteDoc(doc(db, 'games', gameId));
    invalidateGameRecordsCache();
    await writeAuditLog("Delete Game Record", gameId, "Match deleted from system.");
    return { success: true, message: "Game record deleted successfully." };
  } catch (e: any) {
    console.error("[deleteGameRecord] Error:", e);
    return { success: false, message: e.message || "Failed to delete game record." };
  }
}

export async function batchReconcileGameRecords(
  updates: { gameId: string; eventId: string }[]
): Promise<{ success: boolean; message: string; updatedCount: number }> {
  if (!auth.currentUser) return { success: false, message: "Not signed in.", updatedCount: 0 };
  if (!Array.isArray(updates) || updates.length === 0) {
    return { success: true, message: "No updates to reconcile.", updatedCount: 0 };
  }

  try {
    // 1. Firestore batches allow up to 500 writes per batch
    const BATCH_SIZE = 400;
    let totalUpdated = 0;

    for (let i = 0; i < updates.length; i += BATCH_SIZE) {
      const chunk = updates.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);

      for (const item of chunk) {
        if (!item.gameId) continue;
        const ref = doc(db, 'games', item.gameId);
        // Use set with merge: true to avoid failing if document does not exist in Firestore
        batch.set(ref, {
          eventId: item.eventId || '',
          updatedAt: new Date()
        }, { merge: true });
        totalUpdated++;
      }

      await batch.commit();
    }

    // 2. Persist to PostgreSQL backend concurrently and clear server-side memory cache
    try {
      const token = await auth.currentUser?.getIdToken();
      await apiFetch('/api/games/batch-reconcile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(updates.map(u => ({ gameId: u.gameId, eventId: u.eventId || '' })))
      });
    } catch (pgErr) {
      console.warn('[batchReconcileGameRecords] Failed syncing to PostgreSQL backend:', pgErr);
    }

    // 3. Immediately update client-side memory cache if loaded
    if (cachedGameRecords?.data) {
      const updateMap = new Map(updates.map(u => [u.gameId, u.eventId || '']));
      cachedGameRecords.data = cachedGameRecords.data.map(g => {
        if (updateMap.has(g.id)) {
          return { ...g, eventId: updateMap.get(g.id) || '', updatedAt: new Date().toISOString() };
        }
        return g;
      });
    }

    invalidateGameRecordsCache();

    await writeAuditLog(
      "Reconcile Matches to Events",
      "batch_reconcile",
      `Reconciled ${totalUpdated} match records with official club events.`
    );

    return {
      success: true,
      message: `Successfully linked ${totalUpdated} match records to events.`,
      updatedCount: totalUpdated
    };
  } catch (err: any) {
    console.error('[batchReconcileGameRecords] Error:', err);
    return {
      success: false,
      message: err.message || 'Failed to reconcile match records.',
      updatedCount: 0
    };
  }
}

export async function batchSaveGameRecords(
  games: Partial<GameRecord>[]
): Promise<{ success: boolean; message: string; savedCount: number; savedIds: string[]; errors: string[] }> {
  if (!auth.currentUser) return { success: false, message: "Not signed in.", savedCount: 0, savedIds: [], errors: ["Not signed in."] };
  if (!Array.isArray(games) || games.length === 0) {
    return { success: true, message: "No games to save.", savedCount: 0, savedIds: [], errors: [] };
  }

  const BATCH_SIZE = 400;
  let savedCount = 0;
  const savedIds: string[] = [];
  const errors: string[] = [];
  const cleanDocs: any[] = [];

  for (const game of games) {
    try {
      const hands: GameHand[] = (game.hands || []).map((h, idx) => {
        const computed = calculateBidWhistHandPoints({
          biddingTeam: h.biddingTeam,
          bid: h.bid,
          trumpType: h.trumpType,
          outcome: h.outcome,
          isBoston: h.isBoston
        });
        return {
          handNumber: idx + 1,
          biddingTeam: h.biddingTeam,
          bid: Number(h.bid) || 4,
          trumpType: h.trumpType || 'trump',
          direction: h.direction || 'up',
          outcome: h.outcome || 'made',
          isBoston: h.isBoston === true,
          pointsTeam1: computed.pointsTeam1,
          pointsTeam2: computed.pointsTeam2,
          notes: h.notes || ''
        };
      });

      const team1FinalScore = hands.reduce((sum, h) => sum + h.pointsTeam1, 0);
      const team2FinalScore = hands.reduce((sum, h) => sum + h.pointsTeam2, 0);
      const winningTeam = (team1FinalScore >= team2FinalScore) ? 1 : 2;
      const totalBostons = hands.filter(h => h.isBoston).length;

      const participantMemberIds = [
        game.team1Player1Id,
        game.team1Player2Id,
        game.team2Player1Id,
        game.team2Player2Id
      ].filter((id): id is string => !!id && typeof id === 'string');

      const docId = game.id || doc(collection(db, 'games')).id;

      let finalScorecardImageUrl = game.scorecardImageUrl || '';
      if (game.scorecardImageDataUrl) {
        try {
          finalScorecardImageUrl = await uploadScorecardImage(docId, game.scorecardImageDataUrl);
        } catch (uploadErr) {
          console.warn('[batchSaveGameRecords] Failed to upload scorecard image to storage, preserving dataUrl fallback:', uploadErr);
          if (!finalScorecardImageUrl) {
            finalScorecardImageUrl = game.scorecardImageDataUrl;
          }
        }
      }
      if (!finalScorecardImageUrl && game.scorecardImageDataUrl) {
        finalScorecardImageUrl = game.scorecardImageDataUrl;
      }

      const cleanData: any = {
        id: docId,
        playedDate: game.playedDate || new Date().toISOString().slice(0, 10),
        playedTime: game.playedTime || new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
        locationName: game.locationName || 'Clubhouse',
        eventId: game.eventId || '',
        scorecardId: game.scorecardId || '',
        scorecardImageUrl: finalScorecardImageUrl,
        scorecardImageDataUrl: game.scorecardImageDataUrl || (finalScorecardImageUrl.startsWith('data:') ? finalScorecardImageUrl : ''),
        team1Name: game.team1Name || `${game.team1Player1Name || 'P1'} & ${game.team1Player2Name || 'P2'}`,
        team1Player1Id: game.team1Player1Id || '',
        team1Player1Name: game.team1Player1Name || 'Player 1',
        team1Player2Id: game.team1Player2Id || '',
        team1Player2Name: game.team1Player2Name || 'Player 2',
        team2Name: game.team2Name || `${game.team2Player1Name || 'P3'} & ${game.team2Player2Name || 'P4'}`,
        team2Player1Id: game.team2Player1Id || '',
        team2Player1Name: game.team2Player1Name || 'Player 3',
        team2Player2Id: game.team2Player2Id || '',
        team2Player2Name: game.team2Player2Name || 'Player 4',
        participantMemberIds,
        team1FinalScore,
        team2FinalScore,
        winningTeam,
        totalBostons,
        notes: game.notes || '',
        hands,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: game.createdBy || auth.currentUser.uid
      };

      cleanDocs.push(cleanData);
      savedIds.push(docId);
    } catch (prepErr: any) {
      errors.push(`Match prep failed: ${prepErr.message}`);
    }
  }

  // Commit to Firestore in batch chunks
  for (let i = 0; i < cleanDocs.length; i += BATCH_SIZE) {
    const chunk = cleanDocs.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);

    for (const item of chunk) {
      const ref = doc(db, 'games', item.id);
      batch.set(ref, item, { merge: true });
    }

    try {
      await batch.commit();
      savedCount += chunk.length;
    } catch (batchErr: any) {
      console.error('[batchSaveGameRecords] Firestore batch commit failed:', batchErr);
      errors.push(`Firestore batch commit failed: ${batchErr.message}`);
    }
  }

  // Sync to PostgreSQL backend concurrently/resiliently
  try {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      await Promise.allSettled(
        cleanDocs.map(docData =>
          apiFetch('/api/games', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(docData)
          })
        )
      );
    }
  } catch (pgErr) {
    console.warn('[batchSaveGameRecords] Failed syncing some matches to PostgreSQL backend:', pgErr);
  }

  // Invalidate games cache to ensure immediate updates across all leaderboard views
  invalidateGameRecordsCache();

  if (savedCount > 0) {
    await writeAuditLog(
      "Bulk Record Matches",
      "bulk_scan",
      `Bulk saved ${savedCount} match records to leaderboard.`
    );
  }

  return {
    success: savedCount > 0,
    message: `Successfully saved ${savedCount} of ${games.length} matches to leaderboard.`,
    savedCount,
    savedIds,
    errors
  };
}

/**
 * Computes the Wilson Score Interval (lower bound) for binomial confidence.
 * Used by Reddit, Steam, Yelp, and Amazon to rank items with high certainty
 * without arbitrary pseudo-observation assumptions.
 * 
 * @param positive Number of positive outcomes (e.g., matches won or bids made)
 * @param total Total number of trials (e.g., matches played or bids attempted)
 * @param z Z-score confidence level (default: 1.96 for 95% statistical confidence)
 * @returns Lower-bound confidence percentage (0.0 - 100.0)
 */
export function computeWilsonScore(positive: number, total: number, z: number = 1.96): number {
  if (total <= 0 || positive <= 0) return 0;

  const p = positive / total;
  const z2 = z * z;
  const denominator = 1 + (z2 / total);
  const center = p + (z2 / (2 * total));
  const spread = z * Math.sqrt((p * (1 - p) / total) + (z2 / (4 * total * total)));

  const lowerBound = (center - spread) / denominator;
  const clamped = Math.max(0, Math.min(1, lowerBound));
  return Math.round(clamped * 1000) / 10;
}

/**
 * Fast O(1) indexed lookup for user profiles by ID, displayName, nickname, @handle, full name, or first/last names.
 * Replaces linear member array scans that freeze the main UI thread during statistics computations.
 */
export function buildMemberLookup(members: UserProfile[] = []): (id?: string, name?: string) => UserProfile | undefined {
  const byId = new Map<string, UserProfile>();
  const byName = new Map<string, UserProfile>();

  for (let i = 0; i < members.length; i++) {
    const m = members[i];
    if (!m) continue;

    const resolvedPhoto = m.photoUrl || m.avatarUrl || (m as any).photoURL;
    if (resolvedPhoto && typeof resolvedPhoto === 'string' && resolvedPhoto.trim() !== '?' && resolvedPhoto.trim() !== 'null') {
      if (!m.photoUrl) m.photoUrl = resolvedPhoto.trim();
      if (!m.avatarUrl) m.avatarUrl = resolvedPhoto.trim();
    }

    if (m.id) {
      byId.set(m.id.trim(), m);
      const lowerId = m.id.trim().toLowerCase();
      if (!byName.has(lowerId)) byName.set(lowerId, m);
    }

    if (m.displayName) {
      const dn = m.displayName.trim().toLowerCase();
      if (dn && !byName.has(dn)) byName.set(dn, m);
    }

    if (m.nickname) {
      const nick = m.nickname.trim().toLowerCase();
      if (nick) {
        if (!byName.has(nick)) byName.set(nick, m);
        if (!byName.has(`@${nick}`)) byName.set(`@${nick}`, m);
      }
    }

    const fullName = `${m.firstName || ''} ${m.lastName || ''}`.trim().toLowerCase();
    if (fullName && !byName.has(fullName)) {
      byName.set(fullName, m);
    }

    if (m.firstName) {
      const fn = m.firstName.trim().toLowerCase();
      // Bare "pat" requires partner context to distinguish Pat Riley from Pat Hall
      if (fn && fn !== 'pat' && !byName.has(fn)) byName.set(fn, m);
    }

    if (m.lastName) {
      const ln = m.lastName.trim().toLowerCase();
      if (ln && !byName.has(ln)) byName.set(ln, m);
    }
  }

  return (id?: string, name?: string): UserProfile | undefined => {
    if (id) {
      const canonical = MEMBER_ID_ALIASES[id.trim()] || id.trim();
      const direct = byId.get(canonical);
      if (direct) return direct;
      const idLower = byName.get(canonical.toLowerCase());
      if (idLower) return idLower;
    }
    if (name) {
      const clean = name.trim().toLowerCase();
      if (clean) {
        return byName.get(clean);
      }
    }
    return undefined;
  };
}

let cachedLeaderboardStats: {
  gamesRef: GameRecord[];
  membersRef: UserProfile[];
  result: { players: LeaderboardPlayerStat[]; teams: LeaderboardTeamStat[] };
} | null = null;

export function invalidateLeaderboardCache() {
  cachedLeaderboardStats = null;
}

/**
 * Fetches authoritative leaderboard rankings directly from the ASP.NET Core backend.
 */
export async function getLeaderboardStats(
  timeframe: 'all' | 'month' | 'year' = 'all',
  forceRefresh = false
): Promise<{ players: LeaderboardPlayerStat[]; teams: LeaderboardTeamStat[] }> {
  try {
    const url = `/api/leaderboard?timeframe=${encodeURIComponent(timeframe)}${forceRefresh ? '&forceRefresh=true' : ''}`;
    const response = await apiFetch(url);
    if (response.ok) {
      return await response.json();
    }
    console.warn('[getLeaderboardStats] Server returned non-ok status:', response.status);
  } catch (err) {
    console.warn('[getLeaderboardStats] Failed to fetch server leaderboard, falling back:', err);
  }
  return computeLeaderboardStats([], []);
}


/**
 * Order-independent skill rating (Bradley-Terry maximum a-posteriori fit), displayed on the Elo scale.
 * Uses every game at once, so the order games were entered or played in has no effect.
 * A side's strength is the mean of its members' ratings; P(side1 wins) = 1 / (1 + 10^((r2 - r1) / 400)).
 * A weak Gaussian prior centered at 1000 keeps players with few games near 1000 (replaces the old placement boost).
 */
export function fitOrderIndependentRatings(
  games: { side1: string[]; side2: string[]; side1Won: boolean; weight?: number }[],
  priorSdElo = 250
): Map<string, number> {
  const LN10_OVER_400 = Math.LN10 / 400;
  const tauInv2 = 1 / Math.pow(priorSdElo * LN10_OVER_400, 2);
  const s = new Map<string, number>(); // strength offset from 1000 in natural-log units
  for (const g of games) for (const id of [...g.side1, ...g.side2]) s.set(id, 0);

  for (let iter = 0; iter < 2000; iter++) {
    const grad = new Map<string, number>();
    const hess = new Map<string, number>();
    s.forEach((_, id) => { grad.set(id, 0); hess.set(id, 0); });

    for (const g of games) {
      const m1 = g.side1.reduce((a, id) => a + s.get(id)!, 0) / g.side1.length;
      const m2 = g.side2.reduce((a, id) => a + s.get(id)!, 0) / g.side2.length;
      const p = 1 / (1 + Math.exp(-(m1 - m2)));
      const w = g.weight ?? 1;
      const resid = w * ((g.side1Won ? 1 : 0) - p);
      for (const id of g.side1) {
        grad.set(id, grad.get(id)! + resid / g.side1.length);
        hess.set(id, hess.get(id)! + (w * 0.5) / (g.side1.length * g.side1.length));
      }
      for (const id of g.side2) {
        grad.set(id, grad.get(id)! - resid / g.side2.length);
        hess.set(id, hess.get(id)! + (w * 0.5) / (g.side2.length * g.side2.length));
      }
    }

    let maxStep = 0;
    s.forEach((val, id) => {
      const step = (grad.get(id)! - tauInv2 * val) / (hess.get(id)! + tauInv2);
      s.set(id, val + step);
      maxStep = Math.max(maxStep, Math.abs(step));
    });
    if (maxStep < 1e-9) break;
  }

  const out = new Map<string, number>();
  s.forEach((val, id) => out.set(id, Math.round(1000 + val / LN10_OVER_400)));
  return out;
}

/**
 * Derives comprehensive individual player and team partnership rankings with an order-independent Elo-scale rating from recorded games.
 */
export function computeLeaderboardStats(
  games: GameRecord[],
  members: UserProfile[] = []
): { players: LeaderboardPlayerStat[]; teams: LeaderboardTeamStat[] } {
  if (
    cachedLeaderboardStats &&
    cachedLeaderboardStats.gamesRef === games &&
    cachedLeaderboardStats.membersRef === members
  ) {
    return cachedLeaderboardStats.result;
  }

  const findMember = buildMemberLookup(members);

  // 1. Accumulate Individual Player Stats
  interface PlayerAccumulator {
    playerId: string;
    displayName: string;
    nickname?: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    avatarUrl?: string;
    isMember: boolean;
    eloRating: number;
    matchesPlayed: number;
    matchesWon: number;
    matchesLost: number;
    totalPoints: number;
    handsPlayed: number;
    bidsAttempted: number;
    bidsMade: number;
    bidsSet: number;
    totalBidsValue: number;
    bid4Attempted: number;
    bid4Made: number;
    bid5Attempted: number;
    bid5Made: number;
    bid6Attempted: number;
    bid6Made: number;
    bid7Attempted: number;
    bid7Made: number;
    bostons: number;
    handsWon: number;
    defensiveHandsPlayed: number;
    defensiveStops: number;
    uptownHandsPlayed: number;
    uptownHandsWon: number;
    downtownHandsPlayed: number;
    downtownHandsWon: number;
    noTrumpHandsPlayed: number;
    noTrumpHandsWon: number;
    suitTrumpHandsPlayed: number;
    suitTrumpHandsWon: number;
    recentMatches: {
      matchId: string;
      date: string;
      teamName: string;
      partnerName: string;
      opponentNames: string;
      isWin: boolean;
      scoreSummary: string;
      eloDelta?: number;
    }[];
  }

  const playerStatsMap = new Map<string, PlayerAccumulator>();

  const getOrCreatePlayer = (id?: string, fallbackName?: string): PlayerAccumulator => {
    const member = findMember(id, fallbackName);
    const key = member?.id || (id && id.trim()) || (fallbackName || 'Unknown Player').trim();
    if (!playerStatsMap.has(key)) {
      const fullName = [member?.firstName, member?.lastName].filter(Boolean).join(' ') || undefined;
      playerStatsMap.set(key, {
        playerId: key,
        displayName: member?.displayName?.trim() || member?.nickname?.trim() || fullName || fallbackName?.trim() || 'Player',
        nickname: member?.nickname || undefined,
        firstName: member?.firstName || undefined,
        lastName: member?.lastName || undefined,
        fullName,
        avatarUrl: member?.photoUrl || member?.avatarUrl || '',
        isMember: !!member,
        eloRating: 1000,
        matchesPlayed: 0,
        matchesWon: 0,
        matchesLost: 0,
        totalPoints: 0,
        handsPlayed: 0,
        handsWon: 0,
        bidsAttempted: 0,
        bidsMade: 0,
        bidsSet: 0,
        totalBidsValue: 0,
        bid4Attempted: 0,
        bid4Made: 0,
        bid5Attempted: 0,
        bid5Made: 0,
        bid6Attempted: 0,
        bid6Made: 0,
        bid7Attempted: 0,
        bid7Made: 0,
        defensiveHandsPlayed: 0,
        defensiveStops: 0,
        bostons: 0,
        uptownHandsPlayed: 0,
        uptownHandsWon: 0,
        downtownHandsPlayed: 0,
        downtownHandsWon: 0,
        noTrumpHandsPlayed: 0,
        noTrumpHandsWon: 0,
        suitTrumpHandsPlayed: 0,
        suitTrumpHandsWon: 0,
        recentMatches: []
      });
    }
    return playerStatsMap.get(key)!;
  };

  // 2. Accumulate Team Partnership Stats
  interface TeamAccumulator {
    partnershipKey: string;
    teamName: string;
    player1Id?: string;
    player1Name: string;
    player2Id?: string;
    player2Name: string;
    eloRating: number;
    matchesPlayed: number;
    matchesWon: number;
    matchesLost: number;
    totalPoints: number;
    bostons: number;
    handsWon: number;
  }

  const teamStatsMap = new Map<string, TeamAccumulator>();

  const getOrCreateTeam = (
    p1Id?: string,
    p1Name?: string,
    p2Id?: string,
    p2Name?: string,
    customTeamName?: string
  ): TeamAccumulator => {
    const p1Key = (p1Id || p1Name || 'P1').trim();
    const p2Key = (p2Id || p2Name || 'P2').trim();
    const sortedKeys = [p1Key, p2Key].sort();
    const partnershipKey = sortedKeys.join('___');

    if (!teamStatsMap.has(partnershipKey)) {
      teamStatsMap.set(partnershipKey, {
        partnershipKey,
        teamName: customTeamName || `${p1Name || 'Player 1'} & ${p2Name || 'Player 2'}`,
        player1Id: p1Id,
        player1Name: p1Name || 'Player 1',
        player2Id: p2Id,
        player2Name: p2Name || 'Player 2',
        eloRating: 1000,
        matchesPlayed: 0,
        matchesWon: 0,
        matchesLost: 0,
        totalPoints: 0,
        bostons: 0,
        handsWon: 0
      });
    }
    return teamStatsMap.get(partnershipKey)!;
  };

  // Sort games chronologically for accurate Elo history progression
  const sortedGames = [...games].sort((a, b) => {
    const dateA = a.playedDate ? `${a.playedDate} ${a.playedTime || '00:00'}` : (a.id || '');
    const dateB = b.playedDate ? `${b.playedDate} ${b.playedTime || '00:00'}` : (b.id || '');
    return dateA.localeCompare(dateB);
  });

  const PAT_RILEY_MEMBER_ID = '8EKqtb5wH0aHOAJ4BEMnnNf7bHB3';
  const PAT_RILEY_CANONICAL_NAME = 'Pat Riley';
  const PAT_HALL_CANONICAL_NAME = 'Pat Hall';
  const SHEILA_WILLIAMS_MEMBER_ID = 'R5ubwLP85NUYJQ0ojAO8vovKhrL2';
  const WYLIE_WILLIS_MEMBER_ID = 'JU1INvajDgPxYkSiEoPrVo6sFDC2';

  const isSheilaOrWiley = (id?: string, name?: string): boolean => {
    if (id) {
      const cleanId = id.trim();
      if (cleanId === SHEILA_WILLIAMS_MEMBER_ID || cleanId === WYLIE_WILLIS_MEMBER_ID) return true;
    }
    if (!name || !name.trim()) return false;
    const clean = name.trim().toLowerCase();
    return clean.includes('sheila') || clean.includes('shelia') || clean.includes('wiley') || clean.includes('wylie');
  };

  const isPat = (id?: string, name?: string): boolean => {
    if (id && id.trim() === PAT_RILEY_MEMBER_ID) return true;
    if (!name || !name.trim()) return false;
    const clean = name.trim().toLowerCase();
    return clean === 'pat' || clean === 'pat riley' || clean === 'pat hall' || clean.startsWith('pat ');
  };

  const disambiguatePat = (myId?: string, myName?: string, partnerId?: string, partnerName?: string): [string | undefined, string | undefined] => {
    if (!isPat(myId, myName)) return [myId, myName];
    if (isSheilaOrWiley(partnerId, partnerName)) {
      return [PAT_RILEY_MEMBER_ID, PAT_RILEY_CANONICAL_NAME];
    }
    return [undefined, PAT_HALL_CANONICAL_NAME];
  };

  const disambiguatePatPair = (
    p1Id?: string, p1Name?: string,
    p2Id?: string, p2Name?: string
  ): [string | undefined, string | undefined, string | undefined, string | undefined] => {
    const [resolvedP1Id, resolvedP1Name] = disambiguatePat(p1Id, p1Name, p2Id, p2Name);
    const [resolvedP2Id, resolvedP2Name] = disambiguatePat(p2Id, p2Name, p1Id, p1Name);
    return [resolvedP1Id, resolvedP1Name, resolvedP2Id, resolvedP2Name];
  };

  // Process all games deterministically
  for (const game of sortedGames) {
    const [t1p1Id, t1p1Name, t1p2Id, t1p2Name] = disambiguatePatPair(
      game.team1Player1Id, game.team1Player1Name,
      game.team1Player2Id, game.team1Player2Name
    );
    const [t2p1Id, t2p1Name, t2p2Id, t2p2Name] = disambiguatePatPair(
      game.team2Player1Id, game.team2Player1Name,
      game.team2Player2Id, game.team2Player2Name
    );

    const t1p1 = getOrCreatePlayer(t1p1Id, t1p1Name);
    const t1p2 = getOrCreatePlayer(t1p2Id, t1p2Name);
    const t2p1 = getOrCreatePlayer(t2p1Id, t2p1Name);
    const t2p2 = getOrCreatePlayer(t2p2Id, t2p2Name);

    const team1Obj = getOrCreateTeam(
      t1p1Id,
      t1p1Name,
      t1p2Id,
      t1p2Name,
      game.team1Name
    );

    const team2Obj = getOrCreateTeam(
      t2p1Id,
      t2p1Name,
      t2p2Id,
      t2p2Name,
      game.team2Name
    );

    const isT1Win = game.winningTeam === 1;
    const isT2Win = game.winningTeam === 2;
    if (!isT1Win && !isT2Win) continue;

    // --- Dynamic Elo Calculation with Personalized Placement Boost ---
    const avgT1Elo = (t1p1.eloRating + t1p2.eloRating) / 2;
    const avgT2Elo = (t2p1.eloRating + t2p2.eloRating) / 2;

    const expectedT1 = 1 / (1 + Math.pow(10, (avgT2Elo - avgT1Elo) / 400));
    const actualT1 = isT1Win ? 1 : 0;

    // Smooth equilibrium K decay based on games played:
    // Placement phase (0-4 games) starts at K=30, smoothly decaying as volume increases.
    // This prevents attendance grinders from endlessly accumulating points simply by playing more games,
    // allowing high-win-rate players who play fewer games to remain competitive for #1.
    const getDynamicK = (matchesPlayed: number) => Math.max(2.0, 30.0 / (1.0 + 0.10 * matchesPlayed));

    const kT1P1 = getDynamicK(t1p1.matchesPlayed);
    const kT1P2 = getDynamicK(t1p2.matchesPlayed);
    const kT2P1 = getDynamicK(t2p1.matchesPlayed);
    const kT2P2 = getDynamicK(t2p2.matchesPlayed);

    let deltaT1P1 = Math.round(kT1P1 * (actualT1 - expectedT1));
    let deltaT1P2 = Math.round(kT1P2 * (actualT1 - expectedT1));
    let deltaT2P1 = Math.round(kT2P1 * (actualT1 - expectedT1));
    let deltaT2P2 = Math.round(kT2P2 * (actualT1 - expectedT1));

    if (deltaT1P1 === 0) deltaT1P1 = isT1Win ? 1 : -1;
    if (deltaT1P2 === 0) deltaT1P2 = isT1Win ? 1 : -1;
    if (deltaT2P1 === 0) deltaT2P1 = isT1Win ? 1 : -1;
    if (deltaT2P2 === 0) deltaT2P2 = isT1Win ? 1 : -1;

    t1p1.eloRating += deltaT1P1;
    t1p2.eloRating += deltaT1P2;
    t2p1.eloRating -= deltaT2P1;
    t2p2.eloRating -= deltaT2P2;

    // Duo team partnership Elo adjustments
    const expectedDuoT1 = 1 / (1 + Math.pow(10, (team2Obj.eloRating - team1Obj.eloRating) / 400));
    const kDuo = getDynamicK(Math.min(team1Obj.matchesPlayed, team2Obj.matchesPlayed));
    let duoDelta = Math.round(kDuo * (actualT1 - expectedDuoT1));
    if (duoDelta === 0) duoDelta = isT1Win ? 1 : -1;

    team1Obj.eloRating += duoDelta;
    team2Obj.eloRating -= duoDelta;

    // Team 1 match stats
    team1Obj.matchesPlayed += 1;
    team1Obj.totalPoints += game.team1FinalScore;
    if (isT1Win) team1Obj.matchesWon += 1;
    else team1Obj.matchesLost += 1;

    // Team 2 match stats
    team2Obj.matchesPlayed += 1;
    team2Obj.totalPoints += game.team2FinalScore;
    if (isT2Win) team2Obj.matchesWon += 1;
    else team2Obj.matchesLost += 1;

    // Team 1 players match stats
    t1p1.matchesPlayed += 1;
    t1p1.totalPoints += game.team1FinalScore;
    if (isT1Win) t1p1.matchesWon += 1; else t1p1.matchesLost += 1;
    t1p1.recentMatches.push({
      matchId: game.id,
      date: game.playedDate,
      teamName: game.team1Name,
      partnerName: t1p2.displayName,
      opponentNames: `${t2p1.displayName} & ${t2p2.displayName}`,
      isWin: isT1Win,
      scoreSummary: `${game.team1FinalScore} - ${game.team2FinalScore}`,
      eloDelta: deltaT1P1
    });

    t1p2.matchesPlayed += 1;
    t1p2.totalPoints += game.team1FinalScore;
    if (isT1Win) t1p2.matchesWon += 1; else t1p2.matchesLost += 1;
    t1p2.recentMatches.push({
      matchId: game.id,
      date: game.playedDate,
      teamName: game.team1Name,
      partnerName: t1p1.displayName,
      opponentNames: `${t2p1.displayName} & ${t2p2.displayName}`,
      isWin: isT1Win,
      scoreSummary: `${game.team1FinalScore} - ${game.team2FinalScore}`,
      eloDelta: deltaT1P2
    });

    // Team 2 players match stats
    t2p1.matchesPlayed += 1;
    t2p1.totalPoints += game.team2FinalScore;
    if (isT2Win) t2p1.matchesWon += 1; else t2p1.matchesLost += 1;
    t2p1.recentMatches.push({
      matchId: game.id,
      date: game.playedDate,
      teamName: game.team2Name,
      partnerName: t2p2.displayName,
      opponentNames: `${t1p1.displayName} & ${t1p2.displayName}`,
      isWin: isT2Win,
      scoreSummary: `${game.team2FinalScore} - ${game.team1FinalScore}`,
      eloDelta: -deltaT2P1
    });

    t2p2.matchesPlayed += 1;
    t2p2.totalPoints += game.team2FinalScore;
    if (isT2Win) t2p2.matchesWon += 1; else t2p2.matchesLost += 1;
    t2p2.recentMatches.push({
      matchId: game.id,
      date: game.playedDate,
      teamName: game.team2Name,
      partnerName: t2p1.displayName,
      opponentNames: `${t1p1.displayName} & ${t1p2.displayName}`,
      isWin: isT2Win,
      scoreSummary: `${game.team2FinalScore} - ${game.team1FinalScore}`,
      eloDelta: -deltaT2P2
    });

    // Process Hands
    for (const hand of game.hands) {
      const isTeam1Bidding = hand.biddingTeam === 1;
      const biddingPlayers = isTeam1Bidding ? [t1p1, t1p2] : [t2p1, t2p2];
      const defendingPlayers = isTeam1Bidding ? [t2p1, t2p2] : [t1p1, t1p2];
      const biddingTeamObj = isTeam1Bidding ? team1Obj : team2Obj;
      const defendingTeamObj = isTeam1Bidding ? team2Obj : team1Obj;

      const isMade = hand.outcome === 'made';
      const isNT = hand.trumpType === 'no_trump';
      const isUp = hand.direction === 'up';
      const rawP1 = typeof hand.pointsTeam1 === 'number' ? hand.pointsTeam1 : 0;
      const rawP2 = typeof hand.pointsTeam2 === 'number' ? hand.pointsTeam2 : 0;
      const isBoston = isMade && (hand.isBoston === true || Number(hand.bid) === 7 || (isNT && (rawP1 === 14 || rawP2 === 14)));

      // Bostons (Offensive only: bidding team makes the Boston)
      if (isBoston) {
        biddingPlayers.forEach(p => p.bostons += 1);
        biddingTeamObj.bostons += 1;
      }

      // Hands Won
      if (isMade) {
        biddingTeamObj.handsWon += 1;
        biddingPlayers.forEach(p => p.handsWon += 1);
      } else {
        defendingTeamObj.handsWon += 1;
        defendingPlayers.forEach(p => p.handsWon += 1);
      }

      // Hands Played for all 4 players
      [t1p1, t1p2, t2p1, t2p2].forEach(p => {
        p.handsPlayed += 1;
      });

      // Defensive Stats (when opponent held the bid)
      defendingPlayers.forEach(p => {
        p.defensiveHandsPlayed += 1;
        if (!isMade) {
          p.defensiveStops += 1;
        }
      });

      const bidVal = Number(hand.bid) || 4;

      // Bidding & Direction Stats (when player's team held the bid)
      biddingPlayers.forEach(p => {
        p.bidsAttempted += 1;
        p.totalBidsValue += bidVal;
        if (isMade) p.bidsMade += 1;
        else p.bidsSet += 1;

        // Bid Ladder breakdown
        if (bidVal === 4) {
          p.bid4Attempted += 1;
          if (isMade) p.bid4Made += 1;
        } else if (bidVal === 5) {
          p.bid5Attempted += 1;
          if (isMade) p.bid5Made += 1;
        } else if (bidVal === 6) {
          p.bid6Attempted += 1;
          if (isMade) p.bid6Made += 1;
        } else if (bidVal >= 7) {
          p.bid7Attempted += 1;
          if (isMade) p.bid7Made += 1;
        }

        if (isUp) {
          p.uptownHandsPlayed += 1;
          if (isMade) p.uptownHandsWon += 1;
        } else {
          p.downtownHandsPlayed += 1;
          if (isMade) p.downtownHandsWon += 1;
        }

        if (isNT) {
          p.noTrumpHandsPlayed += 1;
          if (isMade) p.noTrumpHandsWon += 1;
        } else {
          p.suitTrumpHandsPlayed += 1;
          if (isMade) p.suitTrumpHandsWon += 1;
        }
      });
    }

    // Match-level totalBostons fallback if hands had unflagged Bostons
    const gameHandBostons = (game.hands || []).filter(h => h.outcome === 'made' && (h.isBoston === true || Number(h.bid) === 7 || (h.trumpType === 'no_trump' && (h.pointsTeam1 === 14 || h.pointsTeam2 === 14)))).length;
    if ((game.totalBostons || 0) > gameHandBostons) {
      const diff = (game.totalBostons || 0) - gameHandBostons;
      const winners = isT1Win ? [t1p1, t1p2] : [t2p1, t2p2];
      const winTeamObj = isT1Win ? team1Obj : team2Obj;
      winners.forEach(p => p.bostons += diff);
      winTeamObj.bostons += diff;
    }
  }

  // 3. Finalize Computations with True Display Rates, Dynamic Elo, and Anti-Camping Status
  const now = new Date();

  const players: LeaderboardPlayerStat[] = Array.from(playerStatsMap.values()).map(p => {
    const matchWinRate = p.matchesPlayed > 0 ? Math.round((p.matchesWon / p.matchesPlayed) * 100) : 0;
    const biddingSuccessRate = p.bidsAttempted > 0 ? Math.round((p.bidsMade / p.bidsAttempted) * 100) : 0;
    const defensiveStopRate = p.defensiveHandsPlayed > 0 ? Math.round((p.defensiveStops / p.defensiveHandsPlayed) * 100) : 0;
    const auctionWinRate = p.handsPlayed > 0 ? Math.round((p.bidsAttempted / p.handsPlayed) * 100) : 0;
    const handWinRate = p.handsPlayed > 0 ? Math.round((p.handsWon / p.handsPlayed) * 100) : 0;
    const avgBidLevel = p.bidsAttempted > 0 ? Math.round((p.totalBidsValue / p.bidsAttempted) * 10) / 10 : 4.0;
    const avgPointsPerMatch = p.matchesPlayed > 0 ? Math.round((p.totalPoints / p.matchesPlayed) * 10) / 10 : 0;

    const uptownWinRate = p.uptownHandsPlayed > 0 ? Math.round((p.uptownHandsWon / p.uptownHandsPlayed) * 100) : 0;
    const downtownWinRate = p.downtownHandsPlayed > 0 ? Math.round((p.downtownHandsWon / p.downtownHandsPlayed) * 100) : 0;
    const noTrumpWinRate = p.noTrumpHandsPlayed > 0 ? Math.round((p.noTrumpHandsWon / p.noTrumpHandsPlayed) * 100) : 0;
    const suitTrumpWinRate = p.suitTrumpHandsPlayed > 0 ? Math.round((p.suitTrumpHandsWon / p.suitTrumpHandsPlayed) * 100) : 0;

    const bidsByLevel = [
      { level: 4, attempted: p.bid4Attempted, made: p.bid4Made, winRate: p.bid4Attempted > 0 ? Math.round((p.bid4Made / p.bid4Attempted) * 100) : 0 },
      { level: 5, attempted: p.bid5Attempted, made: p.bid5Made, winRate: p.bid5Attempted > 0 ? Math.round((p.bid5Made / p.bid5Attempted) * 100) : 0 },
      { level: 6, attempted: p.bid6Attempted, made: p.bid6Made, winRate: p.bid6Attempted > 0 ? Math.round((p.bid6Made / p.bid6Attempted) * 100) : 0 },
      { level: 7, attempted: p.bid7Attempted, made: p.bid7Made, winRate: p.bid7Attempted > 0 ? Math.round((p.bid7Made / p.bid7Attempted) * 100) : 0 }
    ];

    const recentSorted = [...p.recentMatches].reverse();
    const lastPlayedDate = p.recentMatches.reduce((max, m) => (m.date && m.date > max ? m.date : max), '');

    let daysSinceLastMatch = 0;
    if (lastPlayedDate) {
      const lastDate = new Date(lastPlayedDate);
      const diffMs = Math.abs(now.getTime() - lastDate.getTime());
      daysSinceLastMatch = isNaN(diffMs) ? 0 : Math.floor(diffMs / (1000 * 60 * 60 * 24));
    }

    // Anti-Camping & Placement Status (Must complete 5 matches to be officially ranked)
    let activityStatus: 'active' | 'placement' | 'dormant' = 'active';
    if (p.matchesPlayed < 5) {
      activityStatus = 'placement';
    } else if (daysSinceLastMatch > 30) {
      activityStatus = 'dormant';
    } else {
      activityStatus = 'active';
    }

    // Volume Credibility Adjustment:
    // Prevents players with small sample sizes from "freezing" a high win rate and camping atop the leaderboard.
    // A player's rating premium above 1000 is scaled by their sample size confidence: N / (N + 35).
    const credibility = p.matchesPlayed / (p.matchesPlayed + 35.0);
    const rawEloOffset = p.eloRating - 1000.0;
    let shrunkOffset = rawEloOffset * credibility;
    if (rawEloOffset > 0 && shrunkOffset < 1.0) shrunkOffset = 1.0;
    else if (rawEloOffset < 0 && shrunkOffset > -1.0) shrunkOffset = -1.0;

    const bostonMilestones = Math.floor(p.bostons / 5);
    const finalElo = Math.round(1000.0 + shrunkOffset + (bostonMilestones * 8));

    return {
      ...p,
      activityStatus,
      lastPlayedDate,
      daysSinceLastMatch,
      matchWinRate,
      biddingSuccessRate,
      defensiveStopRate,
      auctionWinRate,
      handWinRate,
      avgBidLevel,
      avgPointsPerMatch,
      uptownWinRate,
      downtownWinRate,
      noTrumpWinRate,
      suitTrumpWinRate,
      bidsByLevel,
      recentMatches: recentSorted.slice(0, 5),
      eloRating: finalElo
    };
  }).sort((a, b) => {
    // Official Active Ranked (>= 5 matches) > Placement (< 5 matches) > Dormant (30+ days inactive)
    const getTier = (s: string) => (s === 'active' ? 0 : s === 'placement' ? 1 : 2);
    const tierDiff = getTier(a.activityStatus) - getTier(b.activityStatus);
    if (tierDiff !== 0) return tierDiff;

    // Default ranking: Elo Rating (desc), then Matches Won, then Win Rate, then Points
    if (b.eloRating !== a.eloRating) return b.eloRating - a.eloRating;
    if (b.matchesWon !== a.matchesWon) return b.matchesWon - a.matchesWon;
    if (b.matchWinRate !== a.matchWinRate) return b.matchWinRate - a.matchWinRate;
    if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
    return b.bostons - a.bostons;
  });

  const teams: LeaderboardTeamStat[] = Array.from(teamStatsMap.values()).map(t => {
    const winRate = t.matchesPlayed > 0 ? Math.round((t.matchesWon / t.matchesPlayed) * 100) : 0;

    let activityStatus: 'active' | 'placement' | 'dormant' = 'active';
    if (t.matchesPlayed < 5) activityStatus = 'placement';

    const teamCredibility = t.matchesPlayed / (t.matchesPlayed + 35.0);
    const rawTeamOffset = t.eloRating - 1000.0;
    let shrunkTeamOffset = rawTeamOffset * teamCredibility;
    if (rawTeamOffset > 0 && shrunkTeamOffset < 1.0) shrunkTeamOffset = 1.0;
    else if (rawTeamOffset < 0 && shrunkTeamOffset > -1.0) shrunkTeamOffset = -1.0;

    const teamBostonMilestones = Math.floor(t.bostons / 5);
    const finalElo = Math.round(1000.0 + shrunkTeamOffset + (teamBostonMilestones * 8));

    return {
      ...t,
      activityStatus,
      winRate,
      eloRating: finalElo
    };
  }).sort((a, b) => {
    const getTier = (s: string) => (s === 'active' ? 0 : s === 'placement' ? 1 : 2);
    const tierDiff = getTier(a.activityStatus) - getTier(b.activityStatus);
    if (tierDiff !== 0) return tierDiff;

    if (b.eloRating !== a.eloRating) return b.eloRating - a.eloRating;
    if (b.matchesWon !== a.matchesWon) return b.matchesWon - a.matchesWon;
    if (b.winRate !== a.winRate) return b.winRate - a.winRate;
    return b.totalPoints - a.totalPoints;
  });

  const result = { players, teams };
  cachedLeaderboardStats = { gamesRef: games, membersRef: members, result };
  return result;
}

let cachedArenaLeaderboardStats: {
  gamesRef: ArenaGameRecord[];
  membersRef: UserProfile[];
  modeFilter?: string;
  result: { players: ArenaPlayerStat[] };
} | null = null;

export function invalidateArenaLeaderboardCache() {
  cachedArenaLeaderboardStats = null;
}

export function computeArenaLeaderboardStats(
  arenaGames: ArenaGameRecord[],
  members: UserProfile[] = [],
  modeFilter: 'ALL' | 'PvP' | 'PvE' | 'TOURNAMENT' = 'ALL'
): { players: ArenaPlayerStat[] } {
  if (
    cachedArenaLeaderboardStats &&
    cachedArenaLeaderboardStats.gamesRef === arenaGames &&
    cachedArenaLeaderboardStats.membersRef === members &&
    cachedArenaLeaderboardStats.modeFilter === modeFilter
  ) {
    return cachedArenaLeaderboardStats.result;
  }

  const memberMap = new Map<string, UserProfile>();
  members.forEach(m => memberMap.set(m.id, m));

  const filteredGames = modeFilter === 'ALL'
    ? arenaGames
    : arenaGames.filter(g => g.matchMode === modeFilter);

  interface ArenaPlayerAccumulator {
    playerId: string;
    displayName: string;
    nickname?: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    avatarUrl?: string;
    isMember: boolean;
    isBot?: boolean;
    eloRating: number;
    matchesPlayed: number;
    matchesWon: number;
    matchesLost: number;
    pvpMatchesPlayed: number;
    pvpMatchesWon: number;
    pveMatchesPlayed: number;
    pveMatchesWon: number;
    tournamentMatchesPlayed: number;
    tournamentMatchesWon: number;
    totalPoints: number;
    handsPlayed: number;
    handsWon: number;
    bidsAttempted: number;
    bidsMade: number;
    bidsSet: number;
    bostons: number;
    uptownHandsPlayed: number;
    uptownHandsWon: number;
    downtownHandsPlayed: number;
    downtownHandsWon: number;
    noTrumpHandsPlayed: number;
    noTrumpHandsWon: number;
    suitTrumpHandsPlayed: number;
    suitTrumpHandsWon: number;
    recentMatches: {
      matchId: string;
      date: string;
      matchMode: ArenaMatchMode;
      teamName: string;
      partnerName: string;
      opponentNames: string;
      isWin: boolean;
      scoreSummary: string;
      eloDelta?: number;
    }[];
  }

  const playerStatsMap = new Map<string, ArenaPlayerAccumulator>();
  const findMember = buildMemberLookup(members);

  const getOrCreatePlayer = (id?: string, fallbackName?: string, isBot?: boolean): ArenaPlayerAccumulator => {
    const member = findMember(id, fallbackName);
    const key = member?.id || (id && id.trim()) || (fallbackName || 'Arena Player').trim();
    if (!playerStatsMap.has(key)) {
      const fullName = [member?.firstName, member?.lastName].filter(Boolean).join(' ') || undefined;
      playerStatsMap.set(key, {
        playerId: key,
        displayName: member?.displayName || fullName || fallbackName || 'Arena Player',
        nickname: member?.nickname || undefined,
        firstName: member?.firstName || undefined,
        lastName: member?.lastName || undefined,
        fullName,
        avatarUrl: member?.photoUrl || member?.avatarUrl || '',
        isMember: !!member,
        isBot: !!isBot,
        eloRating: 1000,
        matchesPlayed: 0,
        matchesWon: 0,
        matchesLost: 0,
        pvpMatchesPlayed: 0,
        pvpMatchesWon: 0,
        pveMatchesPlayed: 0,
        pveMatchesWon: 0,
        tournamentMatchesPlayed: 0,
        tournamentMatchesWon: 0,
        totalPoints: 0,
        handsPlayed: 0,
        handsWon: 0,
        bidsAttempted: 0,
        bidsMade: 0,
        bidsSet: 0,
        bostons: 0,
        uptownHandsPlayed: 0,
        uptownHandsWon: 0,
        downtownHandsPlayed: 0,
        downtownHandsWon: 0,
        noTrumpHandsPlayed: 0,
        noTrumpHandsWon: 0,
        suitTrumpHandsPlayed: 0,
        suitTrumpHandsWon: 0,
        recentMatches: []
      });
    }
    return playerStatsMap.get(key)!;
  };

  const sortedGames = [...filteredGames].sort((a, b) => {
    const dateA = `${a.playedDate || ''} ${a.playedTime || '00:00'}`;
    const dateB = `${b.playedDate || ''} ${b.playedTime || '00:00'}`;
    return dateA.localeCompare(dateB);
  });

  for (const game of sortedGames) {
    const t1p1Data = game.team1?.players?.[0];
    const t1p2Data = game.team1?.players?.[1];
    const t2p1Data = game.team2?.players?.[0];
    const t2p2Data = game.team2?.players?.[1];

    const t1p1 = getOrCreatePlayer(t1p1Data?.memberId, t1p1Data?.displayName || 'Player 1', t1p1Data?.isBot);
    const t1p2 = getOrCreatePlayer(t1p2Data?.memberId, t1p2Data?.displayName || 'Player 2', t1p2Data?.isBot);
    const t2p1 = getOrCreatePlayer(t2p1Data?.memberId, t2p1Data?.displayName || 'Player 3', t2p1Data?.isBot);
    const t2p2 = getOrCreatePlayer(t2p2Data?.memberId, t2p2Data?.displayName || 'Player 4', t2p2Data?.isBot);

    const isT1Win = game.winningTeam === 1;
    const isT2Win = game.winningTeam === 2;

    const avgT1Elo = (t1p1.eloRating + t1p2.eloRating) / 2;
    const avgT2Elo = (t2p1.eloRating + t2p2.eloRating) / 2;
    const expectedT1 = 1 / (1 + Math.pow(10, (avgT2Elo - avgT1Elo) / 400));
    const actualT1 = isT1Win ? 1 : 0;

    const isT1Placement = t1p1.matchesPlayed < 5 || t1p2.matchesPlayed < 5;
    const isT2Placement = t2p1.matchesPlayed < 5 || t2p2.matchesPlayed < 5;
    const scoreDiff = Math.abs((game.team1?.finalScore || 0) - (game.team2?.finalScore || 0));
    const bonusMargin = ((game.totalBostons ?? 0) > 0 || scoreDiff >= 15) ? 8 : 0;

    const kFactorT1 = (isT1Placement ? 64 : 32) + bonusMargin;
    const kFactorT2 = (isT2Placement ? 64 : 32) + bonusMargin;

    let t1Delta = Math.round(kFactorT1 * (actualT1 - expectedT1));
    let t2Delta = Math.round(kFactorT2 * (actualT1 - expectedT1));
    if (t1Delta === 0) t1Delta = isT1Win ? 1 : -1;
    if (t2Delta === 0) t2Delta = isT1Win ? 1 : -1;

    t1p1.eloRating += t1Delta;
    t1p2.eloRating += t1Delta;
    t2p1.eloRating -= t2Delta;
    t2p2.eloRating -= t2Delta;

    const mode = game.matchMode || 'PvP';

    [t1p1, t1p2].forEach(p => {
      p.matchesPlayed += 1;
      p.totalPoints += game.team1?.finalScore || 0;
      if (isT1Win) p.matchesWon += 1;
      else p.matchesLost += 1;

      if (mode === 'PvP') {
        p.pvpMatchesPlayed += 1;
        if (isT1Win) p.pvpMatchesWon += 1;
      } else if (mode === 'TOURNAMENT') {
        p.tournamentMatchesPlayed += 1;
        if (isT1Win) p.tournamentMatchesWon += 1;
      } else {
        p.pveMatchesPlayed += 1;
        if (isT1Win) p.pveMatchesWon += 1;
      }

      p.recentMatches.push({
        matchId: game.id,
        date: game.playedDate,
        matchMode: mode,
        teamName: game.team1?.name || 'Team 1',
        partnerName: p.playerId === t1p1.playerId ? t1p2.displayName : t1p1.displayName,
        opponentNames: `${t2p1.displayName} & ${t2p2.displayName}`,
        isWin: isT1Win,
        scoreSummary: `${game.team1?.finalScore || 0} - ${game.team2?.finalScore || 0}`,
        eloDelta: t1Delta
      });
    });

    [t2p1, t2p2].forEach(p => {
      p.matchesPlayed += 1;
      p.totalPoints += game.team2?.finalScore || 0;
      if (isT2Win) p.matchesWon += 1;
      else p.matchesLost += 1;

      if (mode === 'PvP') {
        p.pvpMatchesPlayed += 1;
        if (isT2Win) p.pvpMatchesWon += 1;
      } else if (mode === 'TOURNAMENT') {
        p.tournamentMatchesPlayed += 1;
        if (isT2Win) p.tournamentMatchesWon += 1;
      } else {
        p.pveMatchesPlayed += 1;
        if (isT2Win) p.pveMatchesWon += 1;
      }

      p.recentMatches.push({
        matchId: game.id,
        date: game.playedDate,
        matchMode: mode,
        teamName: game.team2?.name || 'Team 2',
        partnerName: p.playerId === t2p1.playerId ? t2p2.displayName : t2p1.displayName,
        opponentNames: `${t1p1.displayName} & ${t1p2.displayName}`,
        isWin: isT2Win,
        scoreSummary: `${game.team2?.finalScore || 0} - ${game.team1?.finalScore || 0}`,
        eloDelta: -t2Delta
      });
    });

    for (const hand of (game.hands || [])) {
      const isTeam1Bidding = hand.biddingTeam === 1;
      const biddingPlayers = isTeam1Bidding ? [t1p1, t1p2] : [t2p1, t2p2];
      const defendingPlayers = isTeam1Bidding ? [t2p1, t2p2] : [t1p1, t1p2];

      const isMade = hand.outcome === 'made';
      const isNT = hand.trumpType === 'no_trump';
      const isUp = hand.direction === 'up';
      const rawP1 = typeof hand.pointsTeam1 === 'number' ? hand.pointsTeam1 : 0;
      const rawP2 = typeof hand.pointsTeam2 === 'number' ? hand.pointsTeam2 : 0;
      const isBoston = isMade && (hand.isBoston === true || Number(hand.bid) === 7 || (isNT && (rawP1 === 14 || rawP2 === 14)));

      // Bostons (Offensive only: bidding team makes the Boston)
      if (isBoston) {
        biddingPlayers.forEach(p => p.bostons += 1);
      }

      if (isMade) biddingPlayers.forEach(p => p.handsWon += 1);
      else defendingPlayers.forEach(p => p.handsWon += 1);

      [t1p1, t1p2, t2p1, t2p2].forEach(p => p.handsPlayed += 1);

      biddingPlayers.forEach(p => {
        p.bidsAttempted += 1;
        if (isMade) p.bidsMade += 1;
        else p.bidsSet += 1;

        if (isUp) {
          p.uptownHandsPlayed += 1;
          if (isMade) p.uptownHandsWon += 1;
        } else {
          p.downtownHandsPlayed += 1;
          if (isMade) p.downtownHandsWon += 1;
        }

        if (isNT) {
          p.noTrumpHandsPlayed += 1;
          if (isMade) p.noTrumpHandsWon += 1;
        } else {
          p.suitTrumpHandsPlayed += 1;
          if (isMade) p.suitTrumpHandsWon += 1;
        }
      });
    }

    // Match-level totalBostons fallback if hands had unflagged Bostons
    const gameHandBostons = (game.hands || []).filter(h => h.outcome === 'made' && (h.isBoston === true || Number(h.bid) === 7 || (h.trumpType === 'no_trump' && (h.pointsTeam1 === 14 || h.pointsTeam2 === 14)))).length;
    if ((game.totalBostons || 0) > gameHandBostons) {
      const diff = (game.totalBostons || 0) - gameHandBostons;
      const winners = isT1Win ? [t1p1, t1p2] : [t2p1, t2p2];
      winners.forEach(p => p.bostons += diff);
    }
  }

  const now = new Date();
  const players: ArenaPlayerStat[] = Array.from(playerStatsMap.values()).map(p => {
    const matchWinRate = p.matchesPlayed > 0 ? Math.round((p.matchesWon / p.matchesPlayed) * 100) : 0;
    const pvpWinRate = p.pvpMatchesPlayed > 0 ? Math.round((p.pvpMatchesWon / p.pvpMatchesPlayed) * 100) : 0;
    const pveWinRate = p.pveMatchesPlayed > 0 ? Math.round((p.pveMatchesWon / p.pveMatchesPlayed) * 100) : 0;
    const biddingSuccessRate = p.bidsAttempted > 0 ? Math.round((p.bidsMade / p.bidsAttempted) * 100) : 0;
    const uptownWinRate = p.uptownHandsPlayed > 0 ? Math.round((p.uptownHandsWon / p.uptownHandsPlayed) * 100) : 0;
    const downtownWinRate = p.downtownHandsPlayed > 0 ? Math.round((p.downtownHandsWon / p.downtownHandsPlayed) * 100) : 0;
    const noTrumpWinRate = p.noTrumpHandsPlayed > 0 ? Math.round((p.noTrumpHandsWon / p.noTrumpHandsPlayed) * 100) : 0;
    const suitTrumpWinRate = p.suitTrumpHandsPlayed > 0 ? Math.round((p.suitTrumpHandsWon / p.suitTrumpHandsPlayed) * 100) : 0;

    const recentSorted = [...p.recentMatches].reverse();
    const lastPlayedDate = recentSorted[0]?.date || '';

    let daysSinceLastMatch = 0;
    if (lastPlayedDate) {
      const lastDate = new Date(lastPlayedDate);
      const diffMs = Math.abs(now.getTime() - lastDate.getTime());
      daysSinceLastMatch = isNaN(diffMs) ? 0 : Math.floor(diffMs / (1000 * 60 * 60 * 24));
    }

    let activityStatus: 'active' | 'placement' | 'dormant' = 'active';
    if (p.matchesPlayed < 5) {
      activityStatus = 'placement';
    } else if (daysSinceLastMatch > 30) {
      activityStatus = 'dormant';
    } else {
      activityStatus = 'active';
    }

    return {
      ...p,
      activityStatus,
      lastPlayedDate,
      daysSinceLastMatch,
      matchWinRate,
      pvpWinRate,
      pveWinRate,
      biddingSuccessRate,
      uptownWinRate,
      downtownWinRate,
      noTrumpWinRate,
      suitTrumpWinRate,
      recentMatches: recentSorted.slice(0, 10)
    };
  }).sort((a, b) => {
    const getTier = (s: string) => (s === 'active' ? 0 : s === 'placement' ? 1 : 2);
    const tierDiff = getTier(a.activityStatus) - getTier(b.activityStatus);
    if (tierDiff !== 0) return tierDiff;

    if (b.eloRating !== a.eloRating) return b.eloRating - a.eloRating;
    if (b.matchesWon !== a.matchesWon) return b.matchesWon - a.matchesWon;
    if (b.matchWinRate !== a.matchWinRate) return b.matchWinRate - a.matchWinRate;
    if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
    return b.bostons - a.bostons;
  });

  const result = { players };
  cachedArenaLeaderboardStats = { gamesRef: arenaGames, membersRef: members, modeFilter, result };
  return result;
}


// ----------------------------------------------------------------------------
// In-App Arena Game Invitations
// ----------------------------------------------------------------------------

export interface GameInvitationDoc {
  id: string;
  tableId: string;
  tableInviteCode: string;
  tableName: string;
  senderId: string;
  senderName: string;
  senderAvatarUrl?: string;
  recipientId: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  createdAt: number;
  expiresAt: number;
}

export async function sendGameInvitation(params: {
  tableId: string;
  tableInviteCode: string;
  tableName: string;
  senderId: string;
  senderName: string;
  senderAvatarUrl?: string;
  recipientId: string;
}): Promise<string> {
  const invCol = collection(db, 'gameInvitations');
  const invDoc = doc(invCol);
  const now = Date.now();
  const expiresAt = now + 30 * 60 * 1000; // 30 minutes expiration

  await setDoc(invDoc, {
    tableId: params.tableId,
    tableInviteCode: params.tableInviteCode,
    tableName: params.tableName,
    senderId: params.senderId,
    senderName: params.senderName,
    senderAvatarUrl: params.senderAvatarUrl || '',
    recipientId: params.recipientId,
    status: 'pending',
    createdAt: now,
    expiresAt,
  });

  return invDoc.id;
}

export function subscribeToMyGameInvitations(
  recipientId: string,
  onUpdate: (invitations: GameInvitationDoc[]) => void,
  onError?: (err: any) => void
): () => void {
  if (!recipientId) {
    onUpdate([]);
    return () => {};
  }

  const q = query(
    collection(db, 'gameInvitations'),
    where('recipientId', '==', recipientId),
    where('status', '==', 'pending')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const now = Date.now();
      const list: GameInvitationDoc[] = [];
      snapshot.forEach((d) => {
        const data = d.data();
        if (data.expiresAt && data.expiresAt < now) return;
        list.push({
          id: d.id,
          tableId: data.tableId || '',
          tableInviteCode: data.tableInviteCode || '',
          tableName: data.tableName || 'Lounge Arena Table',
          senderId: data.senderId || '',
          senderName: data.senderName || 'A Player',
          senderAvatarUrl: data.senderAvatarUrl || undefined,
          recipientId: data.recipientId || '',
          status: data.status || 'pending',
          createdAt: typeof data.createdAt === 'number' ? data.createdAt : Date.now(),
          expiresAt: typeof data.expiresAt === 'number' ? data.expiresAt : now + 30 * 60 * 1000,
        });
      });
      // Sort newest first
      list.sort((a, b) => b.createdAt - a.createdAt);
      onUpdate(list);
    },
    (err) => {
      console.warn('[GameInvitations] Listener error:', err);
      if (onError) onError(err);
    }
  );
}

export async function respondToGameInvitation(
  inviteId: string,
  status: 'accepted' | 'declined'
): Promise<void> {
  const invRef = doc(db, 'gameInvitations', inviteId);
  await updateDoc(invRef, {
    status,
  });
}
